<template>
  <div class="app-container" style="background: #dcdfe6;" v-if="project">
    <div class="navigate-bar">
      <div class="header">
        <el-button type="text" @click="$router.push('/projects')" class="back-button">
          <i class="el-icon-arrow-left"></i>
        </el-button>
        <h2 class="page-title">{{ project ? project.metadata.name : 'Loading...' }}</h2>
        <p v-if="showDescription" class="page-subtitle">{{ project ? project.metadata.description : 'Project details'}}</p>
        <el-button type="text" @click="toggleDescription" class="description-toggle-button"
          :title="showDescription ? 'Hide description' : 'Show description'">
          <i :class="showDescription ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
        </el-button>
      </div>

      <!-- Module Selection Buttons with Scroll Indicators -->
      <div class="module-buttons-container">
        <div class="scroll-indicator left" :class="{ visible: showLeftArrow }" @click="scrollLeft">
          <i class="el-icon-arrow-left"></i>
        </div>

        <div class="module-buttons" ref="moduleButtons" @scroll="checkScroll">
          <button v-for="module in visibleModules" :key="module.name"
            :class="['module-btn', { active: selectedModule === module.name }]" @click="selectedModule = module.name">
            <i :class="module.icon"></i>
            {{ module.label }}
          </button>
        </div>

        <div class="scroll-indicator right" :class="{ visible: showRightArrow }" @click="scrollRight">
          <i class="el-icon-arrow-right"></i>
        </div>
      </div>
    </div> <!-- End navigate-bar -->

    <div class="content-section">
      <div v-if="selectedModule === 'datasetIntroduction'" class="module-scrollable">
        <div class="dataset-overview">
          <div class="overview-card">
            <div class="summary-stats">
              <div
                class="stat-item"
                v-for="(item, idx) in (project && project.metadata && project.metadata.basicInfo) ? project.metadata.basicInfo : []"
                :key="idx"
              >
                <span class="stat-number">{{ formatStatValue(item.value) }}</span>
                <span class="stat-label">{{ item.name }}</span>
              </div>
            </div>
          </div>
          <div class="overview-card overview-plot-card">
            <div style="padding: 16px; border-bottom: 1px solid #e9ecef;">
              <div style="display: flex; align-items: center; gap: 12px; font-weight: 500; color: #2c3e50;">
                <span>Select Variable:</span>
                <el-select 
                  v-model="overviewParams.selectedVariable" 
                  placeholder="Choose a variable to view distribution"
                  @change="loadOverviewDistribution"
                  style="flex: 1; max-width: 400px;"
                  class="enhanced-select">
                  <el-option 
                    v-for="obs in selectableObsVariables" 
                    :key="obs.name" 
                    :label="formatVariableName(obs.name)" 
                    :value="obs.name">
                    <div class="option-content">
                      <span 
                        class="variable-tag" 
                        :style="{
                          color: getVariableTag(obs.name).color,
                          backgroundColor: getVariableTag(obs.name).bgColor,
                          borderColor: getVariableTag(obs.name).color
                        }">
                        {{ getVariableTag(obs.name).label }}
                      </span>
                      <span class="variable-name">{{ formatVariableName(obs.name) }}</span>
                    </div>
                  </el-option>
                </el-select>
              </div>
            </div>
            <div v-loading="overviewParams.loading" class="overview-plot-container">
              <Plot v-if="overviewParams.selectedVariable && overviewPlot.data.length > 0" 
                :data="overviewPlot.data" 
                :layout="overviewPlot.layout"
                :config="overviewPlot.config" />
              <div v-else class="plot-placeholder">
                <i class="el-icon-info"></i>
                <span>Select a variable to view its distribution</span>
              </div>
            </div>
          </div>
        </div>
        <div class="dataset-description">
          <p>{{ project && project.metadata ? project.metadata.description : '' }}</p>
        </div>
      </div>

      <div v-if="selectedModule === 'differentialDataTable'">
        <div class="module-layout">
          <div class="parameter-panel">
            <h4><i class="el-icon-s-grid"></i> Differential Expression Data</h4>
            <div class="parameter-container">
              <div class="parameter-group">
                <label>Select Genes for Analysis</label>
                <el-select
                  v-model="diffDataParams.selectedGenes"
                  multiple
                  filterable
                  placeholder="Select genes to analyze"
                  style="width: 100%;"
                  :multiple-limit="50"
                >
                  <el-option
                    v-for="gene in diffDataGeneList"
                    :key="gene"
                    :label="gene"
                    :value="gene"
                  />
                </el-select>
                <div style="margin-top: 8px; font-size: 12px; color: #909399;">
                  Selected: {{ diffDataParams.selectedGenes.length }} genes (max 50)
                </div>
              </div>
              <div class="parameter-actions">
                <el-button
                  type="primary"
                  @click="loadDifferentialDataTable"
                  :loading="diffDataResults.loading"
                  :disabled="!project"
                >
                  Load Table
                </el-button>
              </div>
            </div>
          </div>
          
          <div class="results-panel">
            <h4>
              <i class="el-icon-document"></i> Differential Expression Table
              <div style="margin-left: auto; display: flex; gap: 8px; align-items: center;">
                <span v-if="diffDataResults.table && diffDataResults.table.length > 0" style="font-size: 13px; color: #909399; font-weight: normal;">
                  Showing {{ paginatedDiffData.length }} of {{ filteredDiffData.length }} entries
                  <span v-if="filteredDiffData.length < diffDataResults.table.length">
                    (filtered from {{ diffDataResults.table.length }} total)
                  </span>
                </span>
                <el-button
                  v-if="diffDataResults.table && diffDataResults.table.length > 0"
                  size="small"
                  @click="clearAllDiffDataFilters"
                  icon="el-icon-refresh-left"
                >
                  Clear Filters
                </el-button>
                <el-button
                  v-if="diffDataResults.table && diffDataResults.table.length > 0"
                  size="small"
                  @click="downloadDiffDataTable"
                  icon="el-icon-download"
                  type="primary"
                >
                  Download
                </el-button>
              </div>
            </h4>
            <div v-loading="diffDataResults.loading" class="diff-data-table-wrapper">
              <div v-if="diffDataResults.table && diffDataResults.table.length > 0" class="diff-data-full-container">
                <!-- Pagination at top -->
                <div class="diff-data-pagination-top">
                  <el-pagination
                    @size-change="handleDiffDataSizeChange"
                    @current-change="handleDiffDataPageChange"
                    :current-page="diffDataPagination.currentPage"
                    :page-sizes="[10, 25, 50, 100, 200]"
                    :page-size="diffDataPagination.pageSize"
                    layout="total, sizes, prev, pager, next, jumper"
                    :total="filteredDiffData.length"
                  />
                </div>
                
                <!-- Table with scroll -->
                <div class="diff-data-table-scroll">
                  <el-table
                    :data="paginatedDiffData"
                    border
                    stripe
                    style="width: 100%;"
                    height="100%"
                    @selection-change="handleDiffDataSelectionChange"
                    @sort-change="handleDiffDataSortChange"
                    :default-sort="diffDataTableSort"
                  >
                    <el-table-column type="selection" width="55" fixed />
                    <el-table-column
                      v-for="col in diffDataResults.columns"
                      :key="col"
                      :prop="col"
                      :label="col"
                      sortable="custom"
                      :min-width="150"
                      :filters="getColumnFilters(col)"
                      :filter-method="(value, row) => filterDiffDataColumn(value, row, col)"
                      :column-key="col"
                    >
                      <template slot="header">
                        <div style="display: flex; flex-direction: column; gap: 4px;">
                          <span>{{ col }}</span>
                          <el-input
                            v-model="diffDataColumnFilters[col]"
                            size="mini"
                            placeholder="Filter..."
                            @input="applyDiffDataFilters"
                            clearable
                          />
                        </div>
                      </template>
                      <template slot-scope="scope">
                        {{ formatCellValue(scope.row[col]) }}
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </div>
              <div v-else class="plot-placeholder">
                <i class="el-icon-info"></i>
                <p>Click "Load Table" to view the differential expression data</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selectedModule === 'differentialExpression'">
        <div class="module-layout">
          <div class="parameter-panel">
            <h4>
              <i class="el-icon-setting"></i> Parameters
              <span class="filter-btn-wrapper">
                <el-popover placement="right" width="320" trigger="click">
                  <FilterButton :project="project" @change="(f) => { obsFilter = f; onObsFilterChanged(); }" />
                  <button slot="reference" class="filter-icon-btn" :class="{ 'has-filters': Object.keys(obsFilter || {}).length > 0 }">
                    <i class="el-icon-s-operation"></i>
                  </button>
                </el-popover>
              </span>
            </h4>
            <div class="parameter-container de-params">
              <!-- Variable & Groups -->
              <div class="step-section">
                <div class="step-header">
                  <span class="step-title">Variable & Groups</span>
                </div>
                <el-select v-model="deParams.groupby" clearable placeholder="Select variable" size="small" style="width: 100%">
                  <el-option v-for="group in selectableObsVariables" :key="group.name" :label="formatVariableName(group.name)" :value="group.name">
                    <span class="variable-tag" :style="getVariableTagStyle(group.name)">{{ getVariableTag(group.name).label }}</span>
                    <span class="variable-name">{{ formatVariableName(group.name) }}</span>
                  </el-option>
                </el-select>
                <template v-if="deParams.groupby && !deGroupbyIsNumeric()">
                  <el-radio-group v-model="deParams.comparisonType" size="small" class="mt-8 de-comparison-radio">
                    <el-radio-button label="one_vs_rest">One vs Rest</el-radio-button>
                    <el-radio-button label="group_vs_group">Group vs Group</el-radio-button>
                  </el-radio-group>
                  <label class="field-label mt-8">Group 1 <span class="required">*</span></label>
                  <el-select v-model="deParams.group1" :disabled="!deParams.groupby" multiple size="small" style="width: 100%">
                    <el-option v-for="val in deGroupOptions" :key="val" :label="formatDeGroupLabel(val)" :value="val" />
                  </el-select>
                  <template v-if="deParams.comparisonType === 'group_vs_group'">
                    <label class="field-label mt-8">Group 2 <span class="required">*</span></label>
                    <el-select v-model="deParams.group2" :disabled="!deParams.groupby" multiple size="small" style="width: 100%">
                      <el-option v-for="val in deGroup2Options" :key="val" :label="formatDeGroupLabel(val)" :value="val" />
                    </el-select>
                  </template>
                </template>
                <template v-else-if="deParams.groupby && deGroupbyIsNumeric()">
                  <label class="field-label mt-8">Numeric Cutoff <span class="required">*</span></label>
                  <el-radio-group v-model="deParams.numericCutoffMethod" size="small">
                    <el-radio-button label="q1">Q1 (25%)</el-radio-button>
                    <el-radio-button label="q2">Q2 (median)</el-radio-button>
                    <el-radio-button label="q3">Q3 (75%)</el-radio-button>
                  </el-radio-group>
                </template>
              </div>

              <!-- Test & Direction -->
              <div class="step-section">
                <div class="step-header">
                  <span class="step-title">Method & Regulation</span>
                </div>
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
                  <label class="field-label" style="margin: 0;">Method</label>
                  <el-radio-group v-model="deParams.testType" size="small">
                    <el-radio-button label="wilcoxon">Wilcoxon</el-radio-button>
                    <el-radio-button label="t-test">T-test</el-radio-button>
                  </el-radio-group>
                </div>
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 8px;">
                  <label class="field-label" style="margin: 0;">Regulation</label>
                  <el-radio-group v-model="deParams.foldChangeDirection" size="small">
                    <el-radio-button label="positive">Up</el-radio-button>
                    <el-radio-button label="negative">Down</el-radio-button>
                    <el-radio-button label="both">Both</el-radio-button>
                  </el-radio-group>
                </div>
              </div>

            </div>
            
            <!-- Parameter Panel Footer -->
            <div class="parameter-footer">
              <GenesOfInterest :genes="genesOfInterest" :max-visible="20" @remove-gene="handleRemoveGene" @clear-all="clearAllDeGenes" @use-genes="useGenesForFollowing" />
              <el-button size="medium" :loading="deResults.table.loading" @click="runDifferentialExpression"
                type="primary" class="run-analysis-btn" icon="el-icon-caret-right"
                :disabled="!deParams.groupby || (!deGroupbyIsNumeric() ? (deParams.comparisonType === 'group_vs_group' ? (!deParams.group1.length || !deParams.group2.length) : !deParams.group1.length) : false)">
                Run Differential Expression
              </el-button>
            </div>
          </div>
          <div class="results-panel">
            <div class="results-container">
              <!-- Folder-style sub-tabs with actions -->
              <div class="sub-tabs">
                <div class="sub-tabs-left">
                  <button 
                    class="sub-tab" 
                    :class="{ active: activeDeTab === 'volcano' }" 
                    @click="activeDeTab = 'volcano'"
                  >
                    <i class="el-icon-data-analysis"></i> Volcano
                  </button>
                  <button 
                    class="sub-tab" 
                    :class="{ active: activeDeTab === 'table' }" 
                    @click="activeDeTab = 'table'"
                  >
                    <i class="el-icon-document"></i> Table
                  </button>
                </div>
                <div v-if="deSummaryStats" class="sub-tabs-actions">
                  <el-popover v-if="deSummaryStats.venn" placement="bottom" width="320" trigger="click">
                    <VennDiagram :data="deSummaryStats.venn" />
                    <span slot="reference" class="summary-badge summary-badge--venn">
                      <i class="el-icon-pie-chart"></i> Filter Details
                    </span>
                  </el-popover>
                  <span class="summary-badge summary-badge--success summary-badge--clickable" @click="selectAllDeGenes" title="Click to select all significant genes">
                    <i class="el-icon-circle-plus-outline"></i> Select {{ deSummaryStats.significant || 0 }} Significant Genes
                  </span>
                  <button v-if="Array.isArray(deParams.geneInterestList) && deParams.geneInterestList.length" class="action-btn action-btn--danger action-btn--small" @click="clearAllDeGenes">
                    <i class="el-icon-close"></i>
                  </button>
                </div>
              </div>
              <!-- Threshold Controls -->
              <div class="threshold-toolbar">
                <!-- Left section: Fold Change -->
                <div class="threshold-section threshold-section-left">
                  <div class="threshold-item">
                    <DeParamSlider
                      label="|log2FC| ≥"
                      :value="deParams.foldChangeThreshold"
                      :min="0"
                      :max="deParamRanges.foldChangeMax"
                      :step="0.1"
                      :formatter="v => v.toFixed(1)"
                      :tooltip-formatter="formatFoldChangeTooltip"
                      @input="v => { deParams.foldChangeThreshold = v; }"
                    />
                  </div>
                </div>
                <!-- Right section: P-value & FDR -->
                <div class="threshold-section threshold-section-right">
                  <div class="threshold-item threshold-switch">
                    <el-radio-group v-model="pvalueScaleMode" size="small" @change="handlePvalueScaleModeChange">
                      <el-radio-button label="original">Original</el-radio-button>
                      <el-radio-button label="log10">-log10</el-radio-button>
                    </el-radio-group>
                  </div>
                  <div class="threshold-item">
                    <DeParamSlider
                      :label="pvalueScaleMode === 'original' ? 'P-value ≤' : '-log10(P) ≥'"
                      :value="deParams.pValueCutoff"
                      :min="pvalueScaleMode === 'original' ? deParamRanges.pValueMin : deParamRanges.pValueLog10Min"
                      :max="pvalueScaleMode === 'original' ? deParamRanges.pValueMax : deParamRanges.pValueLog10Max"
                      :step="pvalueScaleMode === 'original' ? 0.001 : 0.01"
                      :formatter="pvalueScaleMode === 'original' ? formatPValueDisplay : (v => v.toFixed(2))"
                      :tooltip-formatter="pvalueScaleMode === 'original' ? formatPTooltip : formatLog10PTooltip"
                      @input="v => { deParams.pValueCutoff = v; }"
                    />
                  </div>
                  <div class="threshold-item">
                    <DeParamSlider
                      :label="pvalueScaleMode === 'original' ? 'FDR ≤' : '-log10(FDR) ≥'"
                      :value="deParams.fdrCutoff"
                      :min="pvalueScaleMode === 'original' ? deParamRanges.fdrMin : deParamRanges.fdrLog10Min"
                      :max="pvalueScaleMode === 'original' ? deParamRanges.fdrMax : deParamRanges.fdrLog10Max"
                      :step="pvalueScaleMode === 'original' ? 0.001 : 0.01"
                      :formatter="pvalueScaleMode === 'original' ? formatPValueDisplay : (v => v.toFixed(2))"
                      :tooltip-formatter="pvalueScaleMode === 'original' ? formatPTooltip : formatLog10PTooltip"
                      @input="v => { deParams.fdrCutoff = v; }"
                    />
                  </div>
                </div>
              </div>
              <div v-if="activeDeTab === 'table'" class="table-container">
                <template v-if="deResults.table && deResults.table.loading">
                  <div class="plot-placeholder">
                    <i class="el-icon-loading"></i>
                    <p>Running differential expression...</p>
                    <small>This may take a moment.</small>
                  </div>
                </template>
                <template v-else-if="deFilteredData && deFilteredData.length > 0">
                  <DifferentialExpressionTable 
                    :data="deFilteredData" 
                    @selection-change="handleTableSelection"
                  />
                </template>
                <template v-else>
                  <div class="plot-placeholder">
                    <i class="el-icon-s-data"></i>
                    <p>No differential expression results</p>
                    <small>Run the analysis to populate the table.</small>
                  </div>
                </template>
              </div>

              <div v-if="activeDeTab === 'volcano'" class="plot-container">
                <template v-if="deResults.table && deResults.table.loading">
                  <div class="plot-placeholder">
                    <i class="el-icon-loading"></i>
                    <p>Generating volcano plot...</p>
                    <small>Crunching numbers for visualization.</small>
                  </div>
                </template>
                <template v-else-if="!isPlotEmpty(deVolcanoPlotComputed.data)">
                  <Plot :data="deVolcanoPlotComputed.data" :layout="deVolcanoPlotComputed.layout" :config="deVolcanoPlotComputed.config" />
                </template>
                <template v-else>
                  <div class="plot-placeholder">
                    <i class="el-icon-s-data"></i>
                    <p>Run differential expression to view the volcano plot</p>
                    <small>Choose parameters and click Run.</small>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selectedModule === 'markerGenes'">
        <div class="module-layout">
          <div class="parameter-panel">
            <h4>
              <i class="el-icon-setting"></i> Parameters
              <span class="filter-btn-wrapper">
                <el-popover placement="right" width="320" trigger="click">
                  <FilterButton :project="project" @change="(f) => { obsFilter = f; onObsFilterChanged(); }" />
                  <button slot="reference" class="filter-icon-btn" :class="{ 'has-filters': Object.keys(obsFilter || {}).length > 0 }">
                    <i class="el-icon-s-operation"></i>
                  </button>
                </el-popover>
              </span>
            </h4>
            <div class="parameter-container compact-params">
              <!-- Protein Selection -->
              <div class="step-section">
                <div class="step-header">
                  <i class="el-icon-connection section-icon"></i>
                  <span class="step-title">Proteins</span>
                  <el-tooltip content="Copy to clipboard" placement="top">
                    <el-button type="text" icon="el-icon-document-copy" @click="copyGenes('marker')" class="ml-auto header-btn" />
                  </el-tooltip>
                </div>
                <el-select v-model="markerParams.selectedGenes" multiple filterable clearable remote class="scrollable-select"
                  :reserve-keyword="false" :key="'marker-' + deParams.groupby" :loading="markerParams.loading"
                  :remote-method="fetchMarkerGenes" placeholder="Search proteins (paste supported)"
                  @paste.native="handleGenePaste($event, 'marker')" @change="runMarkerBoxplots">
                  <el-option v-for="option in markerParams.gene_list" :key="option" :label="option" :value="option" />
                </el-select>
              </div>

              <!-- Grouping Variables -->
              <div class="step-section">
                <div class="step-header">
                  <i class="el-icon-menu section-icon"></i>
                  <span class="step-title">Grouping</span>
                  <el-button v-if="markerParams.groupBy && markerParams.subgroupBy" size="mini" type="text" 
                    icon="el-icon-sort" @click="swapMarkerGroupAndSubgroup" class="ml-auto header-btn">Swap</el-button>
                </div>
                <label class="field-label">Primary</label>
                <el-select v-model="markerParams.groupBy" placeholder="Select grouping" size="small" style="width: 100%" @change="runMarkerBoxplots">
                  <el-option v-for="obs in selectableObsVariables" :key="obs.name" :label="formatVariableName(obs.name)" :value="obs.name">
                    <span class="variable-tag" :style="getVariableTagStyle(obs.name)">{{ getVariableTag(obs.name).label }}</span>
                    <span class="variable-name">{{ formatVariableName(obs.name) }}</span>
                  </el-option>
                </el-select>
                <template v-if="markerParams.groupBy">
                  <label class="field-label mt-8">Secondary</label>
                  <el-select v-model="markerParams.subgroupBy" clearable placeholder="Optional" size="small" style="width: 100%"
                    @change="runMarkerBoxplots" @clear="runMarkerBoxplots">
                    <el-option v-for="obs in selectableObsVariables" :key="obs.name" :label="formatVariableName(obs.name)" :value="obs.name">
                      <span class="variable-tag" :style="getVariableTagStyle(obs.name)">{{ getVariableTag(obs.name).label }}</span>
                      <span class="variable-name">{{ formatVariableName(obs.name) }}</span>
                    </el-option>
                  </el-select>
                </template>
              </div>

              <!-- Display Options -->
              <div class="step-section">
                <div class="step-header">
                  <i class="el-icon-s-data section-icon"></i>
                  <span class="step-title">Options</span>
                </div>
                <div class="inline-options">
                  <el-tooltip content="Z-score normalize expression" placement="top">
                    <label class="option-toggle"><el-switch v-model="markerParams.normalize" @change="runMarkerBoxplots" size="small" /><span>Normalize</span></label>
                  </el-tooltip>
                  <el-tooltip content="Show individual data points" placement="top">
                    <label class="option-toggle"><el-switch v-model="markerParams.showJitter" @change="runMarkerBoxplots" size="small" /><span>Points</span></label>
                  </el-tooltip>
                </div>
              </div>
            </div>
          </div>
          <div class="results-panel">
            <h4><i class="el-icon-s-marketing"></i> Marker Proteins Results</h4>
            <div v-if="markerParams.groupBy && (markerParams.selectedGenes && markerParams.selectedGenes.length) && !markerResults.loading" class="results-toolbar">
              <el-input
                v-model="markerParams.search"
                placeholder="Filter Proteins"
                clearable
                size="small"
                style="max-width:320px"
                prefix-icon="el-icon-search"
                @clear="handleMarkerSearchChange"
                @input="handleMarkerSearchChange"
              >
                <template slot="append">
                  <el-button size="small" icon="el-icon-close" @click="markerParams.search=''; handleMarkerSearchChange()" v-if="markerParams.search"></el-button>
                </template>
              </el-input>
              <el-switch v-model="markerParams.showAvg" active-text="Show average Expression" />
              <div style="margin-left:auto;">
                <el-pagination
                  :current-page="markerParams.pagination.pageNum"
                  :page-size="markerParams.pagination.pageSize"
                  :page-sizes="[3,6,9,12]"
                  layout="->, total, sizes, prev, pager, next, jumper"
                  :total="filteredMarkerPlots.length"
                  @current-change="handleMarkerPageChange"
                  @size-change="handleMarkerPageSizeChange"
                />
              </div>
            </div>
            <div class="results-container">
              <div class="plot-sequence">
                <div v-if="!markerParams.groupBy || !(markerParams.selectedGenes && markerParams.selectedGenes.length)"
                  class="plot-item">
                  <div class="plot-placeholder">
                    <i class="el-icon-s-marketing"></i>
                    <p>Select proteins and a grouping variable</p>
                    <small>Choose proteins and group/subgroup in Parameters.</small>
                  </div>
                </div>

                <div v-else-if="markerResults.loading" class="plot-item">
                  <div class="plot-placeholder">
                    <i class="el-icon-loading"></i>
                    <p>Loading boxplots...</p>
                    <small>Generating plots for selected proteins.</small>
                  </div>
                </div>

                <template v-else>
                  <div v-if="markerParams.showAvg && markerResults.avgPlot" class="plot-item plot-item-actual plot-item-average">
                    <Plot :data="markerResults.avgPlot.data" :layout="markerResults.avgPlot.layout" :config="markerResults.avgPlot.config || {}" />
                  </div>
                  <div v-if="markerResults.plots && markerResults.plots.length">
                    <div v-for="plot in pagedMarkerPlots" :key="plot.gene" class="plot-item plot-item-actual">
                      <h5 v-if="false" class="plot-item-gene-title">{{ plot.gene }}</h5>
                      <Plot :data="plot.data" :layout="plot.layout" :config="plot.config || {}" />
                    </div>
                    
                  </div>
                  <div v-else>
                    <div class="plot-placeholder">
                      <i class="el-icon-s-marketing"></i>
                      <p>No boxplots to display</p>
                      <small>Adjust your selection and try again.</small>
                    </div>
                  </div>
                  <div v-if="markerResults.missing && markerResults.missing.length">
                    <h5 class="plot-item-header">Missing proteins</h5>
                    <div style="text-align:center; font-size:12px; color:#6b7280;">
                      {{ markerResults.missing.join(', ') }} not found in dataset
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selectedModule === 'clinicalCorrelation'" data-module="clinicalCorrelation">
        <div class="module-layout">
          <div class="parameter-panel">
            <h4>
              <i class="el-icon-setting"></i> Parameters
              <span class="filter-btn-wrapper">
                <el-popover placement="right" width="320" trigger="click">
                  <FilterButton :project="project" @change="(f) => { obsFilter = f; onObsFilterChanged(); }" />
                  <button slot="reference" class="filter-icon-btn" :class="{ 'has-filters': Object.keys(obsFilter || {}).length > 0 }">
                    <i class="el-icon-s-operation"></i>
                  </button>
                </el-popover>
              </span>
            </h4>
            <div class="parameter-container">
              <div class="parameter-group">
                <div style="display: flex; justify-content: space-between;">
                <label>Select Proteins:</label>
                <el-tooltip content="Copy Selected Proteins" placement="right">
                    <el-button
                      type="primary"
                      plain
                      icon="el-icon-document-copy"
                      @click="copyGenes('clinical')"
                      style="width: 22px; height: 22px; padding: 0; min-width: 22px;"
                    />
                  </el-tooltip>
                </div>
                <el-select class="scrollable-select" v-model="clinicalParams.selectedGenes" multiple filterable clearable remote
                  :reserve-keyword="false" :key="'clinical-' + deParams.groupby" :loading="clinicalParams.loading"
                  :remote-method="fetchClinicalGenes" placeholder="Type to search a protein symbol"
                  @paste.native="handleGenePaste($event, 'clinical')">
                  <el-option v-for="gene in clinicalParams.gene_list" :key="gene" :label="gene"
                    :value="gene"></el-option>
                </el-select>
                <small v-if="(clinicalParams.selectedGenes || []).some(g => globalSelectedGenes.includes(g))"
                  class="gene-hint selected">
                  Selected from differential analysis
                </small>
              </div>
              <div class="parameter-group">
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
                  <label>Correlation Method:</label>
                  <el-radio-group v-model="clinicalParams.correlationMethod" size="small">
                    <el-radio-button label="pearson">Pearson</el-radio-button>
                    <el-radio-button label="spearman">Spearman</el-radio-button>
                  </el-radio-group>
                </div>
              </div>

              <div class="parameter-group">
                <label>Edge |r| threshold (Network):</label>
                <div style="padding: 0 6px;">
                  <el-slider v-model="clinicalParams.networkAbsRThreshold"
                             :min="0" :max="1" :step="0.01"
                             :format-tooltip="val => `|r| ≥ ${val.toFixed(2)}`"
                             class="compact-slider"></el-slider>
                  <div style="display:flex; justify-content:space-between; font-size: 12px; color:#64748b; margin-top: 2px;">
                    <span>0.00</span><span>{{ Number(clinicalParams.networkAbsRThreshold).toFixed(2) }}</span><span>1.00</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
          <div class="results-panel">
            <div class="results-container">
              <!-- Folder-style sub-tabs -->
              <div class="sub-tabs">
                <div class="sub-tabs-left">
                  <button 
                    class="sub-tab" 
                    :class="{ active: activeClinicalTab === 'scatter' }" 
                    @click="activeClinicalTab = 'scatter'"
                  >
                    <i class="el-icon-data-line"></i> Scatter Plot
                  </button>
                  <button 
                    class="sub-tab" 
                    :class="{ active: activeClinicalTab === 'network' }" 
                    @click="activeClinicalTab = 'network'"
                  >
                    <i class="el-icon-share"></i> Correlation Network
                  </button>
                  <button 
                    class="sub-tab" 
                    :class="{ active: activeClinicalTab === 'heatmap' }" 
                    @click="activeClinicalTab = 'heatmap'"
                  >
                    <i class="el-icon-s-grid"></i> Heatmap
                  </button>
                </div>
              </div>
              <div v-if="activeClinicalTab === 'scatter'" class="results-toolbar">
                <div style="display:flex; gap:8px; align-items:center;">
                  <span style="font-size:15px; font-weight:600;">Clinical Variable (Y-axis):</span>
                  <el-select v-model="clinicalParams.clinicalVariable" size="medium" class="enhanced-select">
                    <el-option v-for="obs in selectableObsVariables.filter(o => o.inferred_type === 'numerical')" 
                      :key="obs.name" :label="formatVariableName(obs.name)" :value="obs.name">
                      <div class="option-content">
                        <span class="variable-tag" :style="{
                          color: getVariableTag(obs.name).color,
                          backgroundColor: getVariableTag(obs.name).bgColor,
                          borderColor: getVariableTag(obs.name).color
                        }">{{ getVariableTag(obs.name).label }}</span>
                        <span class="variable-name">{{ formatVariableName(obs.name) }}</span>
                      </div>
                    </el-option>
                  </el-select>
                </div>
                <div v-if="clinicalResults.correlationStats" style="display:flex; gap:12px; align-items:center; margin-left:auto;">
                  <span class="summary-badge summary-badge--info" style="font-size:14px; padding:8px 14px;">
                    <b>R²</b>: {{ clinicalResults.correlationStats.r_squared != null ? clinicalResults.correlationStats.r_squared.toFixed(4) : 'N/A' }}
                  </span>
                  <span class="summary-badge" :class="clinicalResults.correlationStats.p_value != null && clinicalResults.correlationStats.p_value < 0.05 ? 'summary-badge--success' : 'summary-badge--warning'" style="font-size:14px; padding:8px 14px;">
                    <b>p-value</b>: {{ clinicalResults.correlationStats.p_value != null ? (clinicalResults.correlationStats.p_value < 0.0001 ? clinicalResults.correlationStats.p_value.toExponential(2) : clinicalResults.correlationStats.p_value.toFixed(4)) : 'N/A' }}
                  </span>
                </div>
              </div>
              <div v-if="activeClinicalTab === 'scatter'" class="plot-container clinical-scatter-plot">
                <template v-if="!isPlotEmpty(clinicalResults.scatterPlot.data)">
                  <Plot :data="clinicalResults.scatterPlot.data" :layout="clinicalResults.scatterPlot.layout" :config="clinicalResults.scatterPlot.config || {}" />
                </template>
                <template v-else>
                  <div class="plot-placeholder">
                    <i class="el-icon-pie-chart"></i>
                    <p>Select proteins and a clinical variable to see correlation</p>
                    <small>Pick a protein set and variable in Parameters.</small>
                  </div>
                </template>
              </div>
              <div v-if="activeClinicalTab === 'network'" class="plot-container">
                <template v-if="correlationNetworkData.nodes && correlationNetworkData.nodes.length > 0">
                  <div style="width: 100%; height: 100%; position: relative;">
                    <CorrelationNetworkPlot 
                      :networkData="correlationNetworkData"
                      :title="`Clinical–Protein Correlation Network (${clinicalParams.correlationMethod})`"
                    />
                  </div>
                </template>
                <template v-else>
                  <div class="plot-placeholder">
                    <i class="el-icon-share"></i>
                    <p>Select proteins to generate correlation network</p>
                    <small>Choose proteins in Parameters to see their correlations with clinical variables.</small>
                  </div>
                </template>
              </div>
              <div v-if="activeClinicalTab === 'heatmap'" class="plot-container">
                <template v-if="!isPlotEmpty(clinicalResults.heatmap.data)">
                  <div style="align-self:center;">
                    <Plot :data="clinicalResults.heatmap.data" :layout="clinicalResults.heatmap.layout" :config="clinicalResults.heatmap.config" />
                  </div>
                </template>
                <template v-else>
                  <div class="plot-placeholder">
                    <i class="el-icon-pie-chart"></i>
                    <p>Heatmap of correlations will appear here</p>
                    <small>Select proteins to compute correlations to all numerical variables.</small>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selectedModule === 'geneFunction'">
        <div class="module-layout">
          <div class="parameter-panel">
            <h4>
              <i class="el-icon-setting"></i> Parameters
              <span class="filter-btn-wrapper">
                <el-popover placement="right" width="320" trigger="click">
                  <FilterButton :project="project" @change="(f) => { obsFilter = f; onObsFilterChanged(); }" />
                  <button slot="reference" class="filter-icon-btn" :class="{ 'has-filters': Object.keys(obsFilter || {}).length > 0 }">
                    <i class="el-icon-s-operation"></i>
                  </button>
                </el-popover>
              </span>
            </h4>
            <div class="parameter-container">
              <div class="parameter-group">
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
                  <label>Search Mode:</label>
                  <el-radio-group v-model="functionParams.searchMode" size="small" @change="onFunctionModeChange">
                    <el-radio-button label="genes">Protein List</el-radio-button>
                    <el-radio-button label="text">Text Search</el-radio-button>
                  </el-radio-group>
                </div>
              </div>

              <div v-if="functionParams.searchMode === 'genes'" class="parameter-group">
                <div style="display: flex; justify-content: space-between;">
                  <label>Select Proteins:</label>
                  <el-tooltip content="Copy Selected Proteins" placement="right">
                    <el-button
                      type="primary"
                      plain
                      icon="el-icon-document-copy"
                      @click="copyGenes('function')"
                      style="width: 22px; height: 22px; padding: 0; min-width: 22px;"
                    />
                  </el-tooltip>
                </div>
                <el-select class="scrollable-select" v-model="functionParams.selectedGenes" multiple filterable clearable remote
                  :reserve-keyword="false" :key="'function-' + deParams.groupby" :loading="functionParams.loading"
                  :remote-method="fetchGeneFunction" placeholder="Type to search a protein symbol"
                  @paste.native="handleGenePaste($event, 'function')">
                  <el-option v-for="gene in functionParams.gene_list" :key="gene" :label="gene"
                    :value="gene"></el-option>
                </el-select>
                <small v-if="globalSelectedGenes.length > 0" class="gene-hint">
                  {{ globalSelectedGenes.length }} protein(s) selected from differential analysis
                </small>
              </div>

              <div v-if="functionParams.searchMode === 'text'" class="parameter-group">
                <label>Text Search:</label>
                <el-input v-model="functionParams.textQuery" placeholder="e.g. apoptosis, transcription factor, Q9Y2U5"
                          clearable @keyup.enter.native="runGeneFunction" @clear="runGeneFunction">
                </el-input>
                <el-button type="primary" @click="runGeneFunction" :disabled="!canFunctionTextSearch">Search</el-button>
                <small class="gene-hint">Search across protein name, accession, descriptions, GO/KEGG/Reactome, etc.</small>
              </div>
              
              <div class="parameter-group">
                <label>Display Columns:</label>
                <el-select 
                  v-model="functionParams.selectedColumns" 
                  multiple 
                  collapse-tags
                  placeholder="Select columns to display"
                  style="width: 100%"
                >
                  <el-option 
                    v-for="col in availableFunctionColumns" 
                    :key="col.key" 
                    :label="col.label"
                    :value="col.key"
                  >
                  </el-option>
                </el-select>
                <small class="gene-hint">{{ functionParams.selectedColumns.length }} of {{ availableFunctionColumns.length }} columns selected</small>
              </div>
              <div v-if="false" class="parameter-group">
                <label>GO Term Filter:</label>
                <el-select v-model="functionParams.goFilter">
                  <el-option label="Biological Process" value="BP"></el-option>
                  <el-option label="Molecular Function" value="MF"></el-option>
                  <el-option label="Cellular Component" value="CC"></el-option>
                  <el-option label="All" value="all"></el-option>
                </el-select>
              </div>
              <div v-if="false" class="parameter-group">
                <label>Pathway Database:</label>
                <el-select v-model="functionParams.pathwayDB">
                  <el-option label="KEGG" value="kegg"></el-option>
                  <el-option label="Reactome" value="reactome"></el-option>
                  <el-option label="WikiPathways" value="wiki"></el-option>
                </el-select>
              </div>

            </div>
          </div>
          <div class="results-panel">
            <h4><i class="el-icon-collection"></i> Protein Function Results</h4>
            <div class="results-container">
              <div class="table-container">
                <template v-if="functionResults.loading">
                  <div class="plot-placeholder">
                    <i class="el-icon-loading"></i>
                    <p>Loading protein function...</p>
                    <small>Fetching annotations...</small>
                  </div>
                </template>
                <template v-else-if="functionResults.table && functionResults.table.length">
                  <GeneFunctionTable 
                    :data="functionResults.table" 
                    :selected-columns="functionParams.selectedColumns"
                  />
                </template>
                <template v-else>
                  <div class="plot-placeholder">
                    <i class="el-icon-collection"></i>
                    <p>No protein annotations yet</p>
                    <small>Select proteins to populate the table.</small>
                  </div>
                </template>
              </div>
              <el-dialog :title="'Protein details - ' + (geneFunctionDetails.row ? geneFunctionDetails.row.gene_name : '')"
                         :visible.sync="geneFunctionDetails.visible" width="60%" append-to-body>
                <div v-if="geneFunctionDetails.row" class="expand-grid">
                  <div class="kv"><span class="k">Protein name</span><span class="v">{{ geneFunctionDetails.row.gene_name }}</span></div>
                  <div class="kv"><span class="k">Protein accession</span><span class="v">{{ geneFunctionDetails.row.protein_accession }}</span></div>
                  <div class="kv"><span class="k">Protein description</span><span class="v">{{ geneFunctionDetails.row.protein_description }}</span></div>
                  <div class="kv"><span class="k">KOG category</span><span class="v">{{ geneFunctionDetails.row.kog_category }}</span></div>
                  <div class="kv"><span class="k">KOG NO.</span><span class="v">{{ geneFunctionDetails.row.kog_no }}</span></div>
                  <div class="kv"><span class="k">KOG description</span><span class="v">{{ geneFunctionDetails.row.kog_description }}</span></div>
                  <div class="kv"><span class="k">Biological Process</span><span class="v">{{ geneFunctionDetails.row.biological_process }}</span></div>
                  <div class="kv"><span class="k">Cellular Component</span><span class="v">{{ geneFunctionDetails.row.cellular_component }}</span></div>
                  <div class="kv"><span class="k">Molecular Function</span><span class="v">{{ geneFunctionDetails.row.molecular_function }}</span></div>
                  <div class="kv"><span class="k">Pfam ID</span><span class="v">{{ geneFunctionDetails.row.pfam_id }}</span></div>
                  <div class="kv"><span class="k">Domain description</span><span class="v">{{ geneFunctionDetails.row.domain_description }}</span></div>
                  <div class="kv"><span class="k">Subcellular localization</span><span class="v">{{ geneFunctionDetails.row.subcellular_localization }}</span></div>
                  <div class="kv"><span class="k">KEGG KO No.</span><span class="v">{{ geneFunctionDetails.row.kegg_ko_no }}</span></div>
                  <div class="kv"><span class="k">KEGG Gene</span><span class="v">{{ geneFunctionDetails.row.kegg_gene }}</span></div>
                  <div class="kv"><span class="k">KEGG pathway</span><span class="v">{{ geneFunctionDetails.row.kegg_pathway }}</span></div>
                  <div class="kv"><span class="k">HallMark</span><span class="v">{{ geneFunctionDetails.row.hallmark }}</span></div>
                  <div class="kv"><span class="k">msigdbURL</span><span class="v">{{ geneFunctionDetails.row.msigdburl }}</span></div>
                  <div class="kv"><span class="k">Reactome pathway</span><span class="v">{{ geneFunctionDetails.row.reactome_pathway }}</span></div>
                  <div class="kv"><span class="k">Reactome link</span><span class="v">{{ geneFunctionDetails.row.reactome_link }}</span></div>
                  <div class="kv"><span class="k">WikiPathways</span><span class="v">{{ geneFunctionDetails.row.wikipathways }}</span></div>
                  <div class="kv"><span class="k">externalDetailsURL</span><span class="v">{{ geneFunctionDetails.row.externaldetailsurl }}</span></div>
                  <div class="kv"><span class="k">IS_TF</span><span class="v">{{ geneFunctionDetails.row.is_tf }}</span></div>
                </div>
                <span slot="footer" class="dialog-footer">
                  <el-button @click="geneFunctionDetails.visible = false">Close</el-button>
                  <el-button type="primary" @click="copyGeneFunctionRow(geneFunctionDetails.row)">Copy</el-button>
                </span>
              </el-dialog>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selectedModule === 'survivalAnalysis'">
        <div class="module-layout">
          <div class="parameter-panel">
            <h4>
              <i class="el-icon-setting"></i> Parameters
              <span class="filter-btn-wrapper">
                <el-popover placement="right" width="320" trigger="click">
                  <FilterButton :project="project" @change="(f) => { obsFilter = f; onObsFilterChanged(); }" />
                  <button slot="reference" class="filter-icon-btn" :class="{ 'has-filters': Object.keys(obsFilter || {}).length > 0 }">
                    <i class="el-icon-s-operation"></i>
                  </button>
                </el-popover>
              </span>
            </h4>
            <div class="parameter-container">
              <div class="parameter-group">
                <div style="display: flex; justify-content: space-between;">
                <label>Target Proteins:</label>
                <el-tooltip content="Copy Selected Proteins" placement="right">
                    <el-button
                      type="primary"
                      plain
                      icon="el-icon-document-copy"
                      @click="copyGenes('survival')"
                      style="width: 22px; height: 22px; padding: 0; min-width: 22px;"
                    />
                  </el-tooltip>
                </div>
                <el-select class="scrollable-select" v-model="survivalParams.selectedGenes" multiple filterable clearable remote
                  :reserve-keyword="false" :key="'survival-' + deParams.groupby" :loading="survivalParams.loading"
                  :remote-method="fetchSurvivalGenes" placeholder="Type to search a protein symbol"
                  @paste.native="handleGenePaste($event, 'survival')">
                  <el-option v-for="gene in survivalParams.gene_list" :key="gene" :label="gene"
                    :value="gene"></el-option>
                </el-select>
                <small v-if="(survivalParams.selectedGenes || []).some(g => globalSelectedGenes.includes(g))"
                  class="gene-hint selected">
                  Selected from differential analysis
                </small>
              </div>
              <div class="parameter-group">
                <label>AdjustedClinical Variables (Cox):</label>
                <el-select v-model="survivalParams.clinicalVariables" multiple filterable clearable class="enhanced-select">
                  <el-option v-for="obs in survivalClinicalOptions" :key="obs.name" 
                    :label="formatVariableName(obs.name)" :value="obs.name">
                    <div class="option-content">
                      <span class="variable-tag" :style="{
                        color: getVariableTag(obs.name).color,
                        backgroundColor: getVariableTag(obs.name).bgColor,
                        borderColor: getVariableTag(obs.name).color
                      }">{{ getVariableTag(obs.name).label }}</span>
                      <span class="variable-name">{{ formatVariableName(obs.name) }}</span>
                    </div>
                  </el-option>
                </el-select>
              </div>
              <div class="parameter-group">
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
                  <label>Survival Type:</label>
                  <el-radio-group v-model="survivalParams.survivalType" size="small">
                    <el-radio-button label="OS">OS</el-radio-button>
                    <el-radio-button label="PFS">PFS</el-radio-button>
                  </el-radio-group>
                </div>
              </div>
              <div class="parameter-group">
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
                  <label>Expression Cutoff:</label>
                  <el-radio-group v-model="survivalParams.cutoffMethod" size="small">
                    <el-radio-button label="q1">Q1</el-radio-button>
                    <el-radio-button label="q2">Q2</el-radio-button>
                    <el-radio-button label="q3">Q3</el-radio-button>
                  </el-radio-group>
                </div>
              </div>
              <div class="parameter-group">
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
                  <label>Time Unit:</label>
                  <el-radio-group v-model="survivalParams.survivalUnit" size="small">
                    <el-radio-button label="month">Month</el-radio-button>
                    <el-radio-button label="day">Day</el-radio-button>
                  </el-radio-group>
                </div>
              </div>
              <div class="parameter-group">
                <el-checkbox v-model="survivalParams.hideInsignificantGenes">
                  Only Show Significant Proteins (p ≤ 0.05)
                </el-checkbox>
              </div>

            </div>
            
            <!-- Parameter Panel Footer -->
            <div class="parameter-footer">
              <div class="parameter-group">
                <el-button size="medium" :loading="survivalResults.loading" @click="runSurvivalAnalysis"
                  type="primary" class="run-analysis-btn" icon="el-icon-caret-right"
                  :disabled="!survivalParams.selectedGenes || survivalParams.selectedGenes.length === 0">
                  Run Survival Analysis
                </el-button>
              </div>
            </div>
          </div>
          <div class="results-panel">
            <div class="results-container">
              <div class="sub-tabs">
                <div class="sub-tabs-left">
                  <button 
                    class="sub-tab" 
                    :class="{ active: activeSurvivalTab === 'km' }" 
                    @click="activeSurvivalTab = 'km'"
                  >
                    <i class="el-icon-s-order"></i> Kaplan-Meier
                  </button>
                  <button 
                    class="sub-tab" 
                    :class="{ active: activeSurvivalTab === 'forest' }" 
                    @click="activeSurvivalTab = 'forest'"
                  >
                    <i class="el-icon-data-line"></i> Forest Plot
                  </button>
                </div>
              </div>
            <div v-if="activeSurvivalTab === 'km' && survivalResults.plots && survivalResults.plots.length > 0" class="results-toolbar">
              <el-input
                v-model="survivalParams.search"
                placeholder="Filter Genes"
                clearable
                size="small"
                style="max-width:320px"
                prefix-icon="el-icon-search"
                @clear="handleSurvivalSearchChange"
                @input="handleSurvivalSearchChange"
              >
                <template slot="append">
                  <el-button size="small" icon="el-icon-close" @click="survivalParams.search=''; handleSurvivalSearchChange()" v-if="survivalParams.search"></el-button>
                </template>
              </el-input>
              <div style="margin-left:auto;">
                <el-pagination
                  :current-page="survivalParams.pagination.pageNum"
                  :page-size="survivalParams.pagination.pageSize"
                  :page-sizes="[3,6,9,12]"
                  layout="->, total, sizes, prev, pager, next, jumper"
                  :total="filteredSurvivalPlots.length"
                  @current-change="handleSurvivalPageChange"
                  @size-change="handleSurvivalPageSizeChange"
                />
              </div>
            </div>
              <div v-if="activeSurvivalTab === 'km'">
                <template v-if="survivalResults.loading">
                  <div class="plot-placeholder">
                    <i class="el-icon-loading"></i>
                    <p>Calculating survival analysis...</p>
                    <small>This may take a moment.</small>
                  </div>
                </template>
                <template v-else-if="survivalResults.plots && survivalResults.plots.length > 0">
                  <div class="survival-plots-grid">
                    <SurvivalPlot
                      v-for="plot in pagedSurvivalPlots"
                      :key="plot.gene"
                      :gene="plot.gene"
                      :data="plot.data"
                      :layout="plot.layout"
                      :config="plot.config"
                      :stats="plot.stats"
                    />
                  </div>
                </template>
                <template v-else>
                  <div class="plot-placeholder">
                    <i class="el-icon-s-order"></i>
                    <p>Kaplan-Meier curves will appear here</p>
                    <small>Select proteins and click "Run Survival Analysis" button.</small>
                  </div>
                </template>
              </div>
              <div v-if="activeSurvivalTab === 'forest'" class="plot-container">
                <template v-if="survivalResults.loading">
                  <div class="plot-placeholder">
                    <i class="el-icon-loading"></i>
                    <p>Calculating Cox regression analysis...</p>
                    <small>This may take a moment.</small>
                  </div>
                </template>
                <template v-else-if="Array.isArray(survivalResults.coxPlot && survivalResults.coxPlot.data) && (survivalResults.coxPlot.data.length > 0)">
                  <Plot :data="survivalResults.coxPlot.data" :layout="survivalResults.coxPlot.layout || { title: 'Cox Forest' }" :config="survivalResults.coxPlot.config || {}" />
                </template>
                <template v-else>
                  <div class="plot-placeholder">
                    <i class="el-icon-s-order"></i>
                    <p>Forest plot will appear here</p>
                    <small>Click "Run Survival Analysis" to see hazard ratios.</small>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selectedModule === 'functionalEnrichment'">
        <div class="module-layout">
          <div class="parameter-panel">
            <h4>
              <i class="el-icon-setting"></i> Parameters
              <span class="filter-btn-wrapper">
                <el-popover placement="right" width="320" trigger="click">
                  <FilterButton :project="project" @change="(f) => { obsFilter = f; onObsFilterChanged(); }" />
                  <button slot="reference" class="filter-icon-btn" :class="{ 'has-filters': Object.keys(obsFilter || {}).length > 0 }">
                    <i class="el-icon-s-operation"></i>
                  </button>
                </el-popover>
              </span>
            </h4>
            <div class="parameter-container">
              <div class="parameter-group">
                <div style="display: flex; justify-content: space-between;">
                <label>Proteins:</label>
                <el-tooltip content="Copy Selected Proteins" placement="right">
                    <el-button
                      type="primary"
                      plain
                      icon="el-icon-document-copy"
                      @click="copyGenes('enrichment')"
                      style="width: 22px; height: 22px; padding: 0; min-width: 22px;"
                    />
                  </el-tooltip>
                </div>
                <el-select class="scrollable-select" v-model="enrichmentParams.selectedGenes" multiple filterable clearable remote
                          :reserve-keyword="false" :key="'enrich-' + deParams.groupby"
                          :loading="enrichmentParams.loading" :remote-method="fetchEnrichmentGenes"
                          placeholder="Type to search a protein symbol"
                          @paste.native="handleGenePaste($event, 'enrichment')">
                  <el-option v-for="g in enrichmentParams.gene_list" :key="g" :label="g" :value="g" />
                </el-select>
              </div>
              <div class="parameter-group">
                <label>P ≤ 
                  <EditableValue
                    :value="enrichmentParams.pValueCutoff"
                    :formatter="(v) => Number(v).toFixed(3)"
                    :min="0.001"
                    :max="0.1"
                    error-message="P-value must be between 0.001 and 0.1"
                    @change="(v) => { enrichmentParams.pValueCutoff = v; runEnrichment(); }"
                  />
                </label>
                <el-slider v-model="enrichmentParams.pValueCutoff" :min="0.001" :max="0.1" :step="0.001"
                  :format-tooltip="formatPTooltip" class="compact-slider" @change="runEnrichment"></el-slider>
              </div>
              <div class="parameter-group">
                <label>Protein Ratio ≥ 
                  <EditableValue
                    :value="enrichmentParams.geneRatioMin"
                    :formatter="formatPercentTooltip"
                    :min="0"
                    :max="1"
                    :accept-percentage="true"
                    error-message="Protein Ratio must be between 0 and 100%"
                    @change="(v) => { enrichmentParams.geneRatioMin = v; runEnrichment(); }"
                  />
                </label>
                <el-slider v-model="enrichmentParams.geneRatioMin" :min="0" :max="1" :step="0.01"
                  :format-tooltip="formatPercentTooltip" class="compact-slider" @change="runEnrichment"></el-slider>
              </div>
              <div class="parameter-group">
                <label>Overlap Ratio ≥ 
                  <EditableValue
                    :value="enrichmentParams.overlapRatioMin"
                    :formatter="formatPercentTooltip"
                    :min="0"
                    :max="1"
                    :accept-percentage="true"
                    error-message="Overlap Ratio must be between 0 and 100%"
                    @change="(v) => { enrichmentParams.overlapRatioMin = v; runEnrichment(); }"
                  />
                </label>
                <el-slider v-model="enrichmentParams.overlapRatioMin" :min="0" :max="1" :step="0.01"
                  :format-tooltip="formatPercentTooltip" class="compact-slider" @change="runEnrichment"></el-slider>
              </div>
            </div>
          </div>
          <div class="results-panel">
            <div class="results-container">
              <div class="sub-tabs">
                <div class="sub-tabs-left">
                  <button 
                    class="sub-tab" 
                    :class="{ active: activeEnrichmentTab === 'plot' }" 
                    @click="activeEnrichmentTab = 'plot'"
                  >
                    <i class="el-icon-pie-chart"></i> Plot View
                  </button>
                  <button 
                    class="sub-tab" 
                    :class="{ active: activeEnrichmentTab === 'table' }" 
                    @click="activeEnrichmentTab = 'table'"
                  >
                    <i class="el-icon-document"></i> Table View
                  </button>
                </div>
              </div>
              <div class="results-toolbar">
                <div style="display:flex; gap:8px; align-items:center;">
                  <span style="font-size:12px; color:#6b7280;">Pathway Database:</span>
                  <el-select v-model="enrichmentParams.pathwayDatabase" size="small" @change="runEnrichment" style="min-width: 200px;">
                    <el-option label="GO - Biological Process" value="go-bp"></el-option>
                    <el-option label="GO - Cellular Component" value="go-cc"></el-option>
                    <el-option label="GO - Molecular Function" value="go-mf"></el-option>
                    <el-option label="KEGG" value="kegg"></el-option>
                    <el-option label="Reactome" value="reactome"></el-option>
                    <el-option label="WikiPathways" value="wikipathways"></el-option>
                    <el-option label="MSigDB Hallmark" value="msigdb-hallmark"></el-option>
                  </el-select>
                </div>
                <div style="flex:1"></div>
                
<!-- Plot View Controls -->
                <div v-if="activeEnrichmentTab === 'plot'" style="display:flex; gap:12px; align-items:center;">
                  <div style="display:flex; gap:8px; align-items:center;">
                    <span style="font-size:12px; color:#6b7280;">X-axis:</span>
                    <el-select v-model="enrichmentXAxis" size="small" @change="onChangeEnrichmentXAxis" style="width: 160px;">
                      <el-option label="GeneRatio" value="geneRatio"></el-option>
                      <el-option label="log₁₀(Odds Ratio)" value="oddsRatio"></el-option>
                    </el-select>
                  </div>
                  <el-button size="small" @click="exportEnrichmentPng" icon="el-icon-download">
                    <strong>Download Plot</strong>
                  </el-button>
                </div>
                
              </div>
              <div v-if="activeEnrichmentTab === 'plot'" class="enrichment-plot-container">
                <template v-if="enrichmentResults.loading">
                  <div class="plot-placeholder">
                    <i class="el-icon-sugar"></i>
                    <p>Loading enrichment...</p>
                    <small>Fetching pathways and protein sets.</small>
                  </div>
                </template>
                <template v-else-if="enrichmentResults.echartsPlot && enrichmentResults.echartsPlot.pathways && enrichmentResults.echartsPlot.pathways.length > 0">
                  <EnrichmentPlot ref="enrichmentPlotRef" :data="enrichmentResults.echartsPlot" :show-controls="false" :x-axis="enrichmentXAxis" @saved="onEnrichmentPlotSaved" />
                </template>
                <template v-else>
                  <div class="plot-placeholder">
                    <i class="el-icon-sugar"></i>
                    <p>Select proteins to see enrichment results</p>
                    <small>Choose a protein set and database.</small>
                  </div>
                </template>
              </div>

              <div v-if="activeEnrichmentTab === 'table'" class="table-container enrichment-table-wrapper">
                <template v-if="enrichmentResults.loading">
                  <div class="plot-placeholder">
                    <i class="el-icon-sugar"></i>
                    <p>Loading enrichment...</p>
                    <small>Fetching pathways and protein sets.</small>
                  </div>
                </template>
                <template v-else-if="enrichmentResults.table && enrichmentResults.table.length > 0">
                  <EnrichmentTable :data="enrichmentResults.table" />
                </template>
                <template v-else>
                  <div class="plot-placeholder">
                    <i class="el-icon-sugar"></i>
                    <p>Select proteins to see enrichment results</p>
                    <small>Choose a protein set and database.</small>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selectedModule === 'sliceDisplay'">
        <HistopathologySliceBrowser
          :dataset-id="id"
          :project="project"
          :magnifications="sliceMagnifications"
          :obs-filter="obsFilter"
          @obs-filter-change="(f) => { obsFilter = f; }"
        />
      </div>

      <div v-if="selectedModule === 'molecularSubtyping'">
        <div class="module-layout">
          <div class="parameter-panel">
            <h4>
              <i class="el-icon-setting"></i> Subtyping Parameters
              <span class="filter-btn-wrapper">
                <el-popover placement="right" width="320" trigger="click">
                  <FilterButton :project="project" @change="(f) => { obsFilter = f; onObsFilterChanged(); }" />
                  <button slot="reference" class="filter-icon-btn" :class="{ 'has-filters': Object.keys(obsFilter || {}).length > 0 }">
                    <i class="el-icon-s-operation"></i>
                  </button>
                </el-popover>
              </span>
            </h4>
            <div class="parameter-container">
              <div class="parameter-group">
                <label>Subtype Column (Main Grouping):</label>
                <el-select v-model="subtypeParams.subtypeCol" placeholder="Select subtype column" @change="loadAvailableAnnotations">
                  <el-option 
                    v-for="col in availableSubtypeColumns" 
                    :key="col.name" 
                    :label="col.name" 
                    :value="col.name"
                  >
                    <span>{{ col.name }}</span>
                    <span style="color: #8492a6; font-size: 12px; margin-left: 8px;">
                      ({{ col.unique_count }} values)
                    </span>
                  </el-option>
                </el-select>
              </div>
              <div class="parameter-group">
                <label>Additional Annotation Rows:</label>
                <el-select 
                  v-model="subtypeParams.annoVars" 
                  multiple 
                  filterable
                  clearable
                  placeholder="Select additional annotations (optional)"
                  style="width: 100%"
                  :loading="subtypeParams.loadingAnnotations"
                >
                  <el-option 
                    v-for="col in availableAnnotations" 
                    :key="col.name" 
                    :label="col.name" 
                    :value="col.name"
                  >
                    <span>{{ col.name }}</span>
                    <span style="color: #8492a6; font-size: 12px; margin-left: 8px;">
                      ({{ col.unique_count }} values)
                    </span>
                  </el-option>
                </el-select>
                <span class="selected-info">
                  Subtype column always shown first + {{ (subtypeParams.annoVars || []).length }} additional
                </span>
              </div>
              <div class="parameter-group">
                <label>
                  Select Proteins
                  <el-tooltip content="Copy Selected Proteins" placement="top">
                    <el-button type="text" icon="el-icon-document-copy" @click="copySubtypeGenes" size="mini" 
                      :disabled="!subtypeParams.selectedGenes || subtypeParams.selectedGenes.length === 0" />
                  </el-tooltip>
                </label>
                <el-select
                  v-model="subtypeParams.selectedGenes"
                  multiple
                  filterable
                  clearable
                  remote
                  :reserve-keyword="false"
                  :remote-method="(q) => fetchGeneList(q, 'subtypeParams')"
                  :loading="subtypeParams.loading"
                  placeholder="Type to search proteins (leave empty for top variable genes)"
                  style="width: 100%"
                  @paste.native="handleSubtypeGenePaste($event)"
                >
                  <el-option v-for="gene in subtypeParams.gene_list" :key="gene" :label="gene" :value="gene" />
                </el-select>
                <span class="selected-info">
                  {{ (subtypeParams.selectedGenes || []).length }} proteins selected
                  <span v-if="!subtypeParams.selectedGenes || subtypeParams.selectedGenes.length === 0" style="color: #909399;">
                    (will use top {{ subtypeParams.topNGenes }} variable genes)
                  </span>
                </span>
              </div>
              <div class="parameter-group">
                <label>Top Variable Genes = 
                  <EditableValue
                    :value="subtypeParams.topNGenes"
                    :formatter="(v) => (Number(v) === 0 ? 'All' : String(Math.round(v)))"
                    :min="0"
                    :max="2000"
                    :integer="true"
                    error-message="Must be 0 (all genes) or between 50 and 2000"
                    @change="(v) => subtypeParams.topNGenes = v"
                  />
                  <span style="color: #909399; font-size: 12px;"> (0 = all genes; used when no genes selected)</span>
                </label>
                <el-slider v-model="subtypeParams.topNGenes" :min="0" :max="2000" :step="50"
                  :format-tooltip="formatIntTooltip" class="compact-slider"></el-slider>
              </div>
              <div class="parameter-group">
                <label>Z-score Range:</label>
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span>-{{ subtypeParams.zmax }}</span>
                  <el-slider v-model="subtypeParams.zmax" :min="1" :max="5" :step="0.5"
                    style="flex: 1;" :format-tooltip="v => `±${v}`"></el-slider>
                  <span>+{{ subtypeParams.zmax }}</span>
                </div>
              </div>
            </div>
            <div class="parameter-footer">
              <el-button 
                type="primary" 
                @click="runSubtypingHeatmap"
                :loading="subtypeResults.loading"
                :disabled="!subtypeParams.subtypeCol"
              >
                Generate Heatmap
              </el-button>
            </div>
          </div>
          <div class="results-panel">
            <h4>
              <i class="el-icon-coordinate"></i> Molecular Subtyping Heatmap
              <span v-if="subtypeResults.n_samples" style="font-weight: normal; color: #64748b; font-size: 13px; margin-left: 8px;">
                {{ subtypeResults.n_samples }} samples × {{ subtypeResults.n_genes }} genes
              </span>
            </h4>
            <div class="results-container">
              <div v-if="subtypeResults.loading" class="plot-placeholder">
                <i class="el-icon-loading" style="font-size: 36px; animation: spin 1s linear infinite;"></i>
                <p>Generating heatmap...</p>
              </div>
              <div 
                v-else-if="subtypeResults.heatmap && subtypeResults.heatmap.data && subtypeResults.heatmap.data.length > 0"
                class="plot-container"
              >
                <Plot 
                  :data="subtypeResults.heatmap.data" 
                  :layout="subtypeHeatmapLayout" 
                  :config="subtypeResults.heatmap.config || {}"
                />
              </div>
              <div v-else class="plot-placeholder">
                <i class="el-icon-picture-outline"></i>
                <p>Molecular Subtyping Heatmap</p>
                <span>Select a subtype column, annotation rows, and optionally genes, then click Generate Heatmap to visualize.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="selectedModule === 'pcaAnalysis'">
        <div class="module-layout">
          <div class="parameter-panel">
            <h4>
              <i class="el-icon-setting"></i> PCA Parameters
              <span class="filter-btn-wrapper">
                <el-popover placement="right" width="320" trigger="click">
                  <FilterButton :project="project" @change="(f) => { obsFilter = f; onObsFilterChanged(); }" />
                  <button slot="reference" class="filter-icon-btn" :class="{ 'has-filters': Object.keys(obsFilter || {}).length > 0 }">
                    <i class="el-icon-s-operation"></i>
                  </button>
                </el-popover>
              </span>
            </h4>
            <div class="parameter-container">
              <div class="parameter-group">
                <label>Sample Groups:</label>
                <el-select v-model="pcaParams.groupBy" class="enhanced-select">
                  <el-option 
                    v-for="obs in selectableObsVariables" 
                    :key="obs.name" 
                    :label="formatVariableName(obs.name)" 
                    :value="obs.name">
                    <div class="option-content">
                      <span class="variable-tag" :style="{
                        backgroundColor: getVariableTag(obs.name).color + '15',
                        color: getVariableTag(obs.name).color,
                        borderColor: getVariableTag(obs.name).color
                      }">{{ getVariableTag(obs.name).label }}</span>
                      <span class="variable-name">{{ formatVariableName(obs.name) }}</span>
                    </div>
                  </el-option>
                </el-select>
              </div>
              <div class="parameter-group">
                <el-button type="primary" @click="runPCA" :loading="pcaResults.loading" icon="el-icon-data-analysis">
                  Run PCA
                </el-button>
              </div>
            </div>
          </div>
          <div class="results-panel">
            <h4><i class="el-icon-s-data"></i> PCA Results</h4>
            <div v-if="pcaResults.loading" class="loading-container">
              <i class="el-icon-loading"></i> Running PCA analysis...
            </div>
            <div v-else-if="!pcaResults.data || pcaResults.data.length === 0" class="empty-state">
              <i class="el-icon-data-analysis"></i>
              <p>Click "Run PCA" to analyze sample clustering</p>
            </div>
            <div v-else class="plot-container">
              <Plot :data="pcaData" :layout="pcaLayout" :config="pcaConfig" />
            </div>
          </div>
        </div>
      </div>
    </div> <!-- End content-section -->
  </div>
  <div v-else class="project-not-found">
    <h1>Project not found</h1>
    <p>The project ID you entered does not exist or has been removed. Please check the ID and try again.</p>
    <el-button @click="$router.replace({ name: 'projects' })" type="primary">Return to Project List</el-button>
  </div>
</template>

<script>
import axios from 'axios'
import FilterButton from '@/components/Widgets/DatasetFilterButton.vue'
import EditableValue from '@/components/Widgets/InlineEditableNumber.vue'
import DeParamSlider from '@/components/Widgets/DeParamSlider.vue'
import VennDiagram from '@/components/Widgets/VennDiagram.vue'
import CorrelationNetworkPlot from '@/components/Plots/CorrelationNetworkPlot.vue'
import EnrichmentPlot from '@/components/Plots/EnrichmentPlot.vue'
import EnrichmentTable from '@/components/Plots/EnrichmentTable.vue'
import DifferentialExpressionTable from '@/components/Plots/DifferentialExpressionTable.vue'
import GeneFunctionTable from '@/components/Plots/GeneFunctionTable.vue'
import SurvivalPlot from '@/components/Plots/SurvivalPlot.vue'
import GenesOfInterest from '@/components/Widgets/SelectedProteinsPanel.vue'
import HistopathologySliceBrowser from '@/components/AnalysisModules/HistopathologySliceBrowser.vue'

export default {
  name: 'ProjectDetail',
  components: { 
    FilterButton, 
    EditableValue,
    DeParamSlider,
    VennDiagram,
    CorrelationNetworkPlot, 
    EnrichmentPlot, 
    EnrichmentTable,
    DifferentialExpressionTable,
    GeneFunctionTable,
    SurvivalPlot,
    GenesOfInterest,
    HistopathologySliceBrowser
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    },
    sidebarCollapsed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // Description visibility
      showDescription: false,

      // Scroll indicators
      showLeftArrow: false,
      showRightArrow: false,

      // Dynamic header height
      headerHeight: 110, // Initial fallback value

      // Store resize handler for cleanup
      resizeHandler: null,
      resizeDebounceTimer: null,
      // Popstate handler to prevent swipe-back
      popstateHandler: null,
      
      // Reactive property to trigger layout recalculation on window resize
      windowResizeKey: 0,

      project: null,
      selectedModule: 'datasetIntroduction', // Default selected module
      obsFilter: {},
      filteredObsVariables: [], // Filtered obs_variables based on current obsFilter

      activeDeTab: 'volcano', // Active tab in differential expression
      activeClinicalTab: 'scatter', // Active tab in clinical correlation
      activeSurvivalTab: 'km', // Active tab in survival analysis
      modules: [
        { name: 'datasetIntroduction', label: 'Dataset Overview', icon: 'el-icon-info' },
        { name: 'differentialDataTable', label: 'Differential Data', icon: 'el-icon-s-grid', show: false, differentialOnly: true },
        { name: 'differentialExpression', label: 'Differential Expression', icon: 'el-icon-s-data' },
        { name: 'markerGenes', label: 'Marker Proteins', icon: 'el-icon-s-marketing' },
        { name: 'clinicalCorrelation', label: 'Clinical Correlation', icon: 'el-icon-pie-chart' },
        { name: 'geneFunction', label: 'Protein Function', icon: 'el-icon-collection' },
        { name: 'survivalAnalysis', label: 'Survival Analysis', icon: 'el-icon-s-order' },
        { name: 'functionalEnrichment', label: 'Functional Enrichment', icon: 'el-icon-sugar' },
        { name: 'sliceDisplay', label: 'Histopathology Slices', icon: 'el-icon-picture', show:false},
        { name: 'molecularSubtyping', label: 'Molecular Subtyping', icon: 'el-icon-coordinate', show:false},
        { name: 'pcaAnalysis', label: 'PCA', icon: 'el-icon-s-operation', show:false}
      ],
      globalParams: {
        geneInterestList: []
      },
      datasetParams: {
        cancerType: 'all',
        minSampleSize: 1000,
        dataQuality: 'all'
      },
      overviewParams: {
        selectedVariable: '',
        loading: false
      },
      overviewPlot: {
        data: [],
        layout: {},
        config: {}
      },
      deParams: {
        groupby: '',
        group1: [],
        group2: [],
        selectedGenes: [],
        pValueCutoff: 0.05,  // Stored in current scale mode
        fdrCutoff: 0.05,     // Stored in current scale mode
        foldChangeThreshold: 1,
        foldChangeDirection: 'positive', // 'positive', 'negative', 'both'
        comparisonType: 'one_vs_rest',
        testType: 'wilcoxon',
        numericCutoffMethod: 'q2',
        geneInterestList: []
      },
      // P-value/FDR scale mode: 'original' or 'log10'
      pvalueScaleMode: 'original',  // Default to original scale
      // Dynamic ranges for sliders (adapt to data)
      deParamRanges: {
        // Original scale ranges
        pValueMin: 0.001,
        pValueMax: 1,
        fdrMin: 0.001,
        fdrMax: 1,
        // Log10 scale ranges (populated from data)
        pValueLog10Min: 0,
        pValueLog10Max: 10,
        fdrLog10Min: 0,
        fdrLog10Max: 10,
        foldChangeMax: 10
      },
      deResults: {
        rawData: [],  // All genes from backend (unfiltered)
        table: {
          data: [],
          loading: false,
          sort: {},
          pagination: {
            pageSize: 10,
            pageNum: 1,
            total: 0
          }
        },
        geneSymbols: [],
        volcanoPlot: {
          data: [],
          layout: {},
          config: {}
        }
      },
      markerParams: {
        selectedGenes: [],
        gene_list: [],
        loading: false,
        groupBy: '',
        subgroupBy: '',
        normalize: false,
        showJitter: true,
        search: '',
        pagination: {
          pageNum: 1,
          pageSize: 6
        },
        showAvg: false
      },
      markerResults: {
        loading: false,
        plots: [],
        avgPlot: null,
        missing: []
      },
      clinicalParams: {
        selectedGenes: [],
        gene_list: [],
        loading: false,
        clinicalVariable: '',
        correlationMethod: 'pearson',
        networkAbsRThreshold: 0.0,
      },
      
      // Correlation Network Data (populated from backend)
      correlationNetworkData: {
        nodes: [],
        links: [],
        categories: []
      },
      // Raw network (unfiltered) for client-side thresholding
      correlationNetworkRaw: {
        nodes: [],
        links: [],
        categories: []
      },
      clinicalResults: {
        scatterPlot: {
          data: [],
          layout: {},
          config: {}
        },
        circularPlot: {
          data: [],
          layout: {},
          config: {}
        },
        heatmap: {
          data: [],
          layout: {},
          config: {}
        },
        correlationStats: null  // { method, value, r_squared, p_value }
      },
      diffDataParams: {
        selectedGenes: [],
        loading: false
      },
      diffDataResults: {
        table: [],
        columns: [],
        loading: false,
        selectedRows: []
      },
      diffDataGeneList: [],
      diffDataColumnFilters: {},
      diffDataPagination: {
        currentPage: 1,
        pageSize: 25
      },
      diffDataTableSort: {
        prop: null,
        order: null
      },
      functionParams: {
        selectedGenes: [],
        gene_list: [],
        loading: false,
        goFilter: 'all',
        pathwayDB: 'kegg',
        searchMode: 'genes',
        textQuery: '',
        selectedColumns: ['gene_name', 'protein_description', 'biological_process', 'kegg_pathway']
      },
      survivalParams: {
        selectedGenes: [],
        gene_list: [],
        loading: false,
        cutoffMethod: 'q2',
        survivalType: 'OS',
        survivalUnit: 'month',
        clinicalVariables: [],
        hideInsignificantGenes: false,
        search: '',
        pagination: {
          pageNum: 1,
          pageSize: 6
        }
      },
      survivalResults: {
        coxPlot: { data: [], layout: {}, config: {} },
        loading: false,
        kmReqId: 0,
        coxReqId: 0,
        plots: [],
        missing: []
      },
      enrichmentParams: {
        selectedGenes: [],
        pValueCutoff: 0.05,
        pathwayDatabase: 'go-bp', // Combined database and category
        geneRatioMin: 0,
        overlapRatioMin: 0,
        topN: 10,
        gene_list: [],
        loading: false
      },
      sliceMagnifications: [],
      subtypeParams: {
        subtypeCol: '',
        annoVars: [],
        selectedGenes: [],
        // 0 means "all genes" for subtyping heatmap (default).
        topNGenes: 0,
        zmax: 2,
        gene_list: [],
        loading: false,
        loadingAnnotations: false
      },
      subtypeResults: {
        loading: false,
        heatmap: { data: [], layout: {}, config: {} },
        subtype_order: [],
        subtype_colors: {},
        n_samples: 0,
        n_genes: 0,
        gene_list: []
      },
      availableAnnotations: [],
      availableSubtypeColumns: [],
      pcaParams: {
        groupBy: ''
      },
      pcaResults: {
        loading: false,
        data: [],
        layout: {},
        config: {}
      },
      functionResults: {
        loading: false,
        table: [],
        pagination: { pageNum: 1, pageSize: 20, total: 0 }
      },
      enrichmentResults: {
        loading: false,
        bubblePlot: { data: [], layout: {}, config: {} },
        echartsPlot: { pathways: [], data: [], legendCircles: { radii: [], labels: [] } },
        table: []
      },
      activeEnrichmentTab: 'plot',
      geneFunctionDetails: {
        visible: false,
        row: null
      },
      enrichmentXAxis: 'geneRatio'
    }
  },
  computed: {
    // Filter visible modules based on show property and project privacy
    visibleModules() {
      const isDifferentialDataset = this.project?.metadata?.datasetType === 'differential';
      
      return this.modules.filter(module => {
        // Show differential data table only for differential datasets
        if (module.differentialOnly) {
          return isDifferentialDataset;
        }
        
        // Hide certain modules for differential datasets
        if (isDifferentialDataset) {
          // Only show these modules for differential datasets
          const allowedModules = ['datasetIntroduction', 'differentialDataTable', 'geneFunction', 'functionalEnrichment'];
          if (!allowedModules.includes(module.name)) {
            return false;
          }
        }
        
        // If show is explicitly false, only show for private projects
        if (module.show === false) {
          return this.project?.metadata?.datasetType === 'private'
        }
        
        // Hide survival analysis module if no survival data exists
        if (module.name === 'survivalAnalysis') {
          if (!Array.isArray(this.filteredObsVariables)) return false
          const hasSurvivalData = this.filteredObsVariables.some(v => 
            v.name === 'survival_OS_month' || 
            v.name === 'survival_PFS_month' || 
            v.name === 'survival_OS_day' || 
            v.name === 'survival_PFS_day'
          )
          if (!hasSurvivalData) return false
        }
        
        // Hide clinical correlation module if no numerical variables exist
        if (module.name === 'clinicalCorrelation') {
          if (!Array.isArray(this.filteredObsVariables)) return false
          const hasNumericalVars = this.filteredObsVariables.some(v => 
            v.inferred_type === 'numerical'
          )
          if (!hasNumericalVars) return false
        }
        
        // Hide molecular subtyping for public datasets
        if (module.name === 'molecularSubtyping') {
          return this.project?.metadata?.datasetType === 'private'
        }
        
        // Otherwise, show the module
        return true
      })
    },
    
    // Filtered differential data based on column filters
    filteredDiffData() {
      if (!this.diffDataResults.table || this.diffDataResults.table.length === 0) {
        return [];
      }
      
      let filtered = this.diffDataResults.table.slice();
      
      // Apply column filters
      Object.keys(this.diffDataColumnFilters).forEach(col => {
        const filterValue = this.diffDataColumnFilters[col];
        if (filterValue && filterValue.trim()) {
          const filterLower = filterValue.toLowerCase();
          filtered = filtered.filter(row => {
            const cellValue = row[col];
            if (cellValue === null || cellValue === undefined) return false;
            return String(cellValue).toLowerCase().includes(filterLower);
          });
        }
      });
      
      // Apply sorting
      if (this.diffDataTableSort.prop && this.diffDataTableSort.order) {
        const sortProp = this.diffDataTableSort.prop;
        const sortOrder = this.diffDataTableSort.order === 'ascending' ? 1 : -1;
        
        filtered.sort((a, b) => {
          let aVal = a[sortProp];
          let bVal = b[sortProp];
          
          // Handle null/undefined
          if (aVal === null || aVal === undefined) return 1;
          if (bVal === null || bVal === undefined) return -1;
          
          // Try numeric comparison first
          const aNum = Number(aVal);
          const bNum = Number(bVal);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return (aNum - bNum) * sortOrder;
          }
          
          // String comparison
          return String(aVal).localeCompare(String(bVal)) * sortOrder;
        });
      }
      
      return filtered;
    },
    
    // Paginated differential data
    paginatedDiffData() {
      const start = (this.diffDataPagination.currentPage - 1) * this.diffDataPagination.pageSize;
      const end = start + this.diffDataPagination.pageSize;
      return this.filteredDiffData.slice(start, end);
    },
    // Genes of Interest - consolidated from deParams
    genesOfInterest() {
      return Array.isArray(this.deParams.geneInterestList) ? this.deParams.geneInterestList : []
    },
    // Filter out categorical variables with only one unique value, grouped by name prefix
    selectableObsVariables() {
      if (!Array.isArray(this.filteredObsVariables)) return []
      const filtered = this.filteredObsVariables.filter(v => {
        // Keep numerical variables (always selectable)
        if (v.inferred_type === 'numerical') return true
        // For categorical variables, only keep if they have more than one unique value
        return Array.isArray(v.unique_values) && v.unique_values.length > 1
      })
      // Group by name prefix: meta_ (categorical) first, then numerical_, then survival_, then others
      // Retain original order within each group
      const meta = filtered.filter(v => v.name?.startsWith('meta_'))
      const numerical = filtered.filter(v => v.name?.startsWith('numerical_'))
      const survival = filtered.filter(v => v.name?.startsWith('survival_'))
      const others = filtered.filter(v => !v.name?.startsWith('meta_') && !v.name?.startsWith('numerical_') && !v.name?.startsWith('survival_'))
      return [...meta, ...numerical, ...survival, ...others]
    },
    // Available columns for gene function table
    availableFunctionColumns() {
      return [
        { key: 'gene_name', label: 'Gene Name' },
        { key: 'protein_accession', label: 'Protein Accession' },
        { key: 'protein_description', label: 'Protein Description' },
        { key: 'kog_category', label: 'KOG Category' },
        { key: 'kog_no', label: 'KOG NO.' },
        { key: 'kog_description', label: 'KOG Description' },
        { key: 'biological_process', label: 'Biological Process' },
        { key: 'cellular_component', label: 'Cellular Component' },
        { key: 'molecular_function', label: 'Molecular Function' },
        { key: 'pfam_id', label: 'Pfam ID' },
        { key: 'domain_description', label: 'Domain Description' },
        { key: 'subcellular_localization', label: 'Subcellular Localization' },
        { key: 'kegg_ko_no', label: 'KEGG KO No.' },
        { key: 'kegg_gene', label: 'KEGG Gene' },
        { key: 'kegg_pathway', label: 'KEGG Pathway' },
        { key: 'hallmark', label: 'HallMark' },
        { key: 'msigdburl', label: 'MSigDB URL' },
        { key: 'reactome_pathway', label: 'Reactome Pathway' },
        { key: 'reactome_link', label: 'Reactome Link' },
        { key: 'wikipathways', label: 'WikiPathways' },
        { key: 'externaldetailsurl', label: 'External Details URL' },
        { key: 'is_tf', label: 'IS_TF' }
      ]
    },
    
    canFunctionTextSearch() {
      return this.functionParams.searchMode === 'text' && !!String(this.functionParams.textQuery || '').trim() && !this.functionResults.loading
    },
    pagedFunctionTable() {
      const rows = Array.isArray(this.functionResults.table) ? this.functionResults.table : []
      const pg = this.functionResults && this.functionResults.pagination ? this.functionResults.pagination : { pageNum: 1, pageSize: 20 }
      const start = (pg.pageNum - 1) * pg.pageSize
      const end = start + pg.pageSize
      return rows.slice(start, end)
    },
    survivalClinicalOptions() {
      if (!Array.isArray(this.selectableObsVariables)) return []
      // Exclude primary keys and survival time/status columns themselves
      const exclude = new Set([
        'survival_status',
        'survival_progression',
        'survival_OS_month',
        'survival_PFS_month',
        'survival_OS_day',
        'survival_PFS_day'
      ])
      return this.selectableObsVariables.filter(o => !exclude.has(o.name))
    },
    globalSelectedGenes() {
      return Array.isArray(this.globalParams && this.globalParams.geneInterestList)
        ? this.globalParams.geneInterestList
        : [];
    },

    // Paged and sorted DE table data (client-side) - uses filtered data
    pagedDeTableData() {
      const { pagination } = this.deResults.table;
      let rows = [...this.deFilteredData];  // Use filtered data from computed property
      const sort = this.deResults.table.sort || {};
      if (sort.prop) {
        const factor = sort.order === 'descending' ? -1 : 1;
        rows.sort((a, b) => {
          const va = a[sort.prop];
          const vb = b[sort.prop];
          if (va == null && vb == null) return 0;
          if (va == null) return 1;
          if (vb == null) return -1;
          if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * factor;
          return String(va).localeCompare(String(vb)) * factor;
        });
      }
      const start = (pagination.pageNum - 1) * pagination.pageSize;
      const end = start + pagination.pageSize;
      return rows.slice(start, end);
    },

    // Get current thresholds in regular scale (for filtering)
    deCurrentThresholds() {
      const pval = this.pvalueScaleMode === 'log10' 
        ? Math.pow(10, -this.deParams.pValueCutoff)
        : this.deParams.pValueCutoff;
      const fdr = this.pvalueScaleMode === 'log10'
        ? Math.pow(10, -this.deParams.fdrCutoff)
        : this.deParams.fdrCutoff;
      return {
        pValue: pval,
        fdr: fdr,
        foldChange: this.deParams.foldChangeThreshold,
        direction: this.deParams.foldChangeDirection
      };
    },

    // Filter raw DE data based on current thresholds
    deFilteredData() {
      const raw = this.deResults.rawData || [];
      if (!raw.length) return [];
      
      const { pValue, fdr, foldChange, direction } = this.deCurrentThresholds;
      
      return raw.filter(gene => {
        // P-value filter
        if (gene.pval != null && gene.pval > pValue) return false;
        // FDR filter
        if (gene.FDR != null && gene.FDR > fdr) return false;
        // Fold change filter with direction
        if (gene.log2FC != null) {
          const fc = gene.log2FC;
          if (direction === 'positive') {
            if (fc < foldChange) return false;
          } else if (direction === 'negative') {
            if (fc > -foldChange) return false;
          } else {
            if (Math.abs(fc) < foldChange) return false;
          }
        }
        return true;
      });
    },

    // Calculate summary statistics from raw data
    deSummaryStats() {
      const raw = this.deResults.rawData || [];
      if (!raw.length) return null;
      
      const { pValue, fdr, foldChange, direction } = this.deCurrentThresholds;
      
      // Direction mask
      const passesDirection = (gene) => {
        if (direction === 'positive') return gene.log2FC > 0;
        if (direction === 'negative') return gene.log2FC < 0;
        return true;
      };
      
      // Individual masks
      const passesPval = (gene) => gene.pval != null && gene.pval <= pValue && passesDirection(gene);
      const passesFdr = (gene) => gene.FDR != null && gene.FDR <= fdr && passesDirection(gene);
      const passesFc = (gene) => {
        if (gene.log2FC == null) return false;
        const fc = gene.log2FC;
        if (direction === 'positive') return fc >= foldChange;
        if (direction === 'negative') return fc <= -foldChange;
        return Math.abs(fc) >= foldChange;
      };
      
      let pOnly = 0, fdrOnly = 0, fcOnly = 0;
      let pAndFdr = 0, pAndFc = 0, fdrAndFc = 0;
      let allThree = 0, none = 0;
      
      raw.forEach(gene => {
        const p = passesPval(gene);
        const f = passesFdr(gene);
        const fc = passesFc(gene);
        
        if (p && f && fc) allThree++;
        else if (p && f && !fc) pAndFdr++;
        else if (p && !f && fc) pAndFc++;
        else if (!p && f && fc) fdrAndFc++;
        else if (p && !f && !fc) pOnly++;
        else if (!p && f && !fc) fdrOnly++;
        else if (!p && !f && fc) fcOnly++;
        else none++;
      });
      
      const significant = allThree;
      const pvalOnlyCount = pOnly + pAndFc;  // P-value but not fully significant
      const fdrOnlyCount = fdrOnly + fdrAndFc;  // FDR but not fully significant
      
      return {
        totalTested: raw.length,
        significant: significant,
        pvalOnly: pvalOnlyCount,
        fdrOnly: fdrOnlyCount,
        notSignificant: none,
        venn: {
          pval: pOnly + pAndFdr + pAndFc + allThree,
          fdr: fdrOnly + pAndFdr + fdrAndFc + allThree,
          fc: fcOnly + pAndFc + fdrAndFc + allThree,
          pOnly, fdrOnly, fcOnly,
          pAndFdr, pAndFc, fdrAndFc,
          allThree, none
        }
      };
    },

    // Build volcano plot data client-side
    deVolcanoPlotComputed() {
      const raw = this.deResults.rawData || [];
      if (!raw.length) return { data: [], layout: {}, config: {} };
      
      const { pValue, fdr, foldChange, direction } = this.deCurrentThresholds;
      
      // Colors matching summary badges
      const sigColor = '#16a34a';    // Green - Significant
      const pOnlyColor = '#d97706';  // Amber - P-value Only
      const fdrOnlyColor = '#ea580c'; // Orange - FDR Only
      const nsColor = '#94a3b8';     // Slate - Not Significant
      
      // Categorize each gene
      const categories = {
        sigUp: [], sigDown: [],
        pOnlyUp: [], pOnlyDown: [],
        fdrOnlyUp: [], fdrOnlyDown: [],
        ns: []
      };
      
      raw.forEach(gene => {
        const geneSymbol = gene.geneSymbol || gene.gene || 'Unknown';
        const log2fc = gene.log2FC;
        const pval = gene.pval;
        const fdrVal = gene.FDR;
        
        if (log2fc == null || pval == null) return;
        
        const negLogP = pval > 0 ? -Math.log10(pval) : 0;
        const isUp = log2fc > 0;
        
        // Check direction filter
        const passDir = direction === 'both' || 
          (direction === 'positive' && isUp) || 
          (direction === 'negative' && !isUp);
        
        const passesPval = pval <= pValue && passDir;
        const passesFdr = fdrVal != null && fdrVal <= fdr && passDir;
        const passesFc = direction === 'positive' ? log2fc >= foldChange :
                        direction === 'negative' ? log2fc <= -foldChange :
                        Math.abs(log2fc) >= foldChange;
        
        const point = { x: log2fc, y: negLogP, text: geneSymbol, pval, fdr: fdrVal };
        
        if (passesPval && passesFdr && passesFc) {
          if (isUp) categories.sigUp.push(point);
          else categories.sigDown.push(point);
        } else if (passesPval && !passesFdr && passesFc) {
          if (isUp) categories.pOnlyUp.push(point);
          else categories.pOnlyDown.push(point);
        } else if (!passesPval && passesFdr && passesFc) {
          if (isUp) categories.fdrOnlyUp.push(point);
          else categories.fdrOnlyDown.push(point);
        } else {
          categories.ns.push(point);
        }
      });
      
      const makeTrace = (points, name, color) => ({
        x: points.map(p => p.x),
        y: points.map(p => p.y),
        text: points.map(p => p.text),
        customdata: points.map(p => [p.pval, p.fdr]),
        mode: 'markers',
        type: 'scatter',
        name: name,
        marker: { color: color, size: 6, opacity: 0.7 },
        hovertemplate: '<b>%{text}</b><br>log2FC: %{x:.2f}<br>-log10(p): %{y:.2f}<br>p-value: %{customdata[0]:.2e}<br>FDR: %{customdata[1]:.2e}<extra></extra>'
      });
      
      const traces = [
        makeTrace(categories.sigUp, 'Significant (Up)', sigColor),
        makeTrace(categories.sigDown, 'Significant (Down)', sigColor),
        makeTrace(categories.pOnlyUp, 'P-value Only (Up)', pOnlyColor),
        makeTrace(categories.pOnlyDown, 'P-value Only (Down)', pOnlyColor),
        makeTrace(categories.fdrOnlyUp, 'FDR Only (Up)', fdrOnlyColor),
        makeTrace(categories.fdrOnlyDown, 'FDR Only (Down)', fdrOnlyColor),
        makeTrace(categories.ns, 'Not Significant', nsColor)
      ].filter(t => t.x.length > 0);
      
      // Add threshold lines
      const maxY = Math.max(...raw.map(g => g.pval > 0 ? -Math.log10(g.pval) : 0), 1);
      const minX = Math.min(...raw.map(g => g.log2FC || 0), -1);
      const maxX = Math.max(...raw.map(g => g.log2FC || 0), 1);
      
      const shapes = [
        // P-value threshold line
        { type: 'line', x0: minX - 1, x1: maxX + 1, y0: -Math.log10(pValue), y1: -Math.log10(pValue), 
          line: { color: '#ef4444', width: 1, dash: 'dash' } }
      ];
      
      // Fold change threshold lines
      if (direction !== 'negative') {
        shapes.push({ type: 'line', x0: foldChange, x1: foldChange, y0: 0, y1: maxY + 1, 
          line: { color: '#3b82f6', width: 1, dash: 'dash' } });
      }
      if (direction !== 'positive') {
        shapes.push({ type: 'line', x0: -foldChange, x1: -foldChange, y0: 0, y1: maxY + 1, 
          line: { color: '#3b82f6', width: 1, dash: 'dash' } });
      }
      
      return {
        data: traces,
        layout: {
          xaxis: { 
            title: { 
              text: '<b>log<sub>2</sub>(Fold Change)</b>', 
              font: { size: 14, color: '#374151' },
              standoff: 5
            }, 
            zeroline: true, 
            zerolinecolor: '#ccc',
            showline: true,
            linecolor: '#000000',
            linewidth: 1,
            mirror: true
          },
          yaxis: { 
            title: { 
              text: '<b>-log<sub>10</sub>(p-value)</b>', 
              font: { size: 14, color: '#374151' },
              standoff: 5
            }, 
            rangemode: 'tozero',
            showline: true,
            linecolor: '#000000',
            linewidth: 1,
            mirror: true
          },
          shapes: shapes,
          legend: { orientation: 'h', y: -0.15 },
          margin: { t: 50, r: 30, b: 80, l: 70 },
          hovermode: 'closest'
        },
        config: { 
          responsive: true, 
          displayModeBar: true,
          modeBarButtons: [['toImage', 'resetScale2d']],
          displaylogo: false
        }
      };
    },

    // Differential Expression: options for groups based on selected variable
    deGroupOptions() {
      if (!Array.isArray(this.selectableObsVariables) || !this.deParams.groupby) return [];
      const variable = this.selectableObsVariables.find(v => v.name === this.deParams.groupby);
      if (!variable || !Array.isArray(variable.unique_values)) return [];
      return variable.unique_values;
    },
    deGroup2Options() {
      if (!Array.isArray(this.deGroupOptions)) return [];
      const selected = Array.isArray(this.deParams.group1) ? this.deParams.group1 : [];
      return this.deGroupOptions.filter(v => !selected.includes(v));
    },

    // Marker plots: filter and paginate on frontend
    filteredMarkerPlots() {
      const plots = Array.isArray(this.markerResults && this.markerResults.plots) ? this.markerResults.plots : [];
      const q = String(this.markerParams && this.markerParams.search || '').trim().toLowerCase();
      if (!q) return plots;
      return plots.filter(p => {
        const g = (p && (p.gene || p.name || ''));
        return String(g).toLowerCase().includes(q);
      });
    },
    pagedMarkerPlots() {
      const list = this.filteredMarkerPlots;
      const pageSize = (this.markerParams && this.markerParams.pagination && this.markerParams.pagination.pageSize) || 6;
      const pageNum = (this.markerParams && this.markerParams.pagination && this.markerParams.pagination.pageNum) || 1;
      const start = (pageNum - 1) * pageSize;
      const end = start + pageSize;
      return list.slice(start, end);
    },

    // Survival plots: filter and paginate on frontend
    filteredSurvivalPlots() {
      const plots = Array.isArray(this.survivalResults && this.survivalResults.plots) ? this.survivalResults.plots : [];
      const q = String(this.survivalParams && this.survivalParams.search || '').trim().toLowerCase();
      if (!q) return plots;
      return plots.filter(p => {
        const g = (p && (p.gene || p.name || ''));
        return String(g).toLowerCase().includes(q);
      });
    },
    pagedSurvivalPlots() {
      const list = this.filteredSurvivalPlots;
      const pageSize = (this.survivalParams && this.survivalParams.pagination && this.survivalParams.pagination.pageSize) || 6;
      const pageNum = (this.survivalParams && this.survivalParams.pagination && this.survivalParams.pagination.pageNum) || 1;
      const start = (pageNum - 1) * pageSize;
      const end = start + pageSize;
      return list.slice(start, end);
    },

    // PCA 2D Plot
    pcaData() {
      return this.pcaResults.data || []
    },

    pcaLayout() {
      return this.pcaResults.layout || {
        title: 'PCA Analysis - Sample Clustering',
        xaxis: { title: 'PC1' },
        yaxis: { title: 'PC2' },
        height: 500,
        hovermode: 'closest'
      }
    },

    pcaConfig() {
      return this.pcaResults.config || {}
    },

    // Molecular Subtyping Heatmap Layout
    subtypeHeatmapLayout() {
      const baseLayout = this.subtypeResults.heatmap?.layout || {};
      return {
        ...baseLayout,
        height: Math.max(600, 80 + (this.subtypeParams.annoVars?.length || 0) * 25 + 400),
        autosize: true
      }
    },

  },

  methods: {
    async loadDifferentialDataTable() {
      if (!this.project) return;
      
      this.diffDataResults.loading = true;
      try {
        const response = await fetch(`/api/projectDetail/get_differential_data?dataset_id=${this.id}`);
        const res = await response.json();
        
        if (res.code === 200 && res.data) {
          this.diffDataResults.table = res.data.table || [];
          this.diffDataResults.columns = res.data.columns || [];
          
          // Initialize column filters
          this.diffDataColumnFilters = {};
          this.diffDataResults.columns.forEach(col => {
            this.$set(this.diffDataColumnFilters, col, '');
          });
          
          // Reset pagination and sorting
          this.diffDataPagination = {
            currentPage: 1,
            pageSize: 25
          };
          this.diffDataTableSort = {
            prop: null,
            order: null
          };
          
          // Extract gene list for selection
          if (this.diffDataResults.table.length > 0 && this.diffDataResults.columns.length > 0) {
            const firstCol = this.diffDataResults.columns[0];
            this.diffDataGeneList = this.diffDataResults.table.map(row => row[firstCol]);
          }
          
          this.$notify({
            title: 'Success',
            message: `Loaded ${this.diffDataResults.table.length} genes`,
            type: 'success',
            position: 'top-right'
          });
        } else {
          this.$notify({
            title: 'Error',
            message: res.error || 'Failed to load differential data',
            type: 'error',
            position: 'top-right'
          });
        }
      } catch (e) {
        this.$notify({
          title: 'Error',
          message: 'Failed to load differential data: ' + e.message,
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.diffDataResults.loading = false;
      }
    },
    
    handleDiffDataSelectionChange(selection) {
      this.diffDataResults.selectedRows = selection;
      // Update selected genes from table selection
      if (selection.length > 0 && this.diffDataResults.columns.length > 0) {
        const firstCol = this.diffDataResults.columns[0];
        this.diffDataParams.selectedGenes = selection.map(row => row[firstCol]);
      } else {
        this.diffDataParams.selectedGenes = [];
      }
    },
    
    downloadDiffDataTable() {
      if (!this.diffDataResults.table || this.diffDataResults.table.length === 0) return;
      
      // Convert table to TSV (use filtered data)
      const columns = this.diffDataResults.columns;
      const rows = this.filteredDiffData; // Download filtered data
      
      let tsv = columns.join('\t') + '\n';
      rows.forEach(row => {
        const values = columns.map(col => row[col] !== null && row[col] !== undefined ? row[col] : '');
        tsv += values.join('\t') + '\n';
      });
      
      // Create download
      const blob = new Blob([tsv], { type: 'text/tab-separated-values' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.project.metadata.name}_differential_data.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
    
    // Differential data table pagination and filtering methods
    handleDiffDataPageChange(page) {
      this.diffDataPagination.currentPage = page;
    },
    
    handleDiffDataSizeChange(size) {
      this.diffDataPagination.pageSize = size;
      this.diffDataPagination.currentPage = 1; // Reset to first page
    },
    
    handleDiffDataSortChange({ prop, order }) {
      this.diffDataTableSort.prop = prop;
      this.diffDataTableSort.order = order;
    },
    
    applyDiffDataFilters() {
      // Reset to first page when filters change
      this.diffDataPagination.currentPage = 1;
    },
    
    clearAllDiffDataFilters() {
      this.diffDataColumnFilters = {};
      this.diffDataTableSort = { prop: null, order: null };
      this.diffDataPagination.currentPage = 1;
    },
    
    getColumnFilters() {
      // Not used for custom header input, but required for el-table filter
      return [];
    },
    
    filterDiffDataColumn() {
      // Not used since we have custom filtering, but required by el-table
      return true;
    },
    
    formatCellValue(value) {
      if (value === null || value === undefined) return '';
      
      // Format numbers with scientific notation if very small or very large
      const num = Number(value);
      if (!isNaN(num)) {
        if (Math.abs(num) < 0.0001 && num !== 0) {
          return num.toExponential(3);
        } else if (Math.abs(num) > 1000000) {
          return num.toExponential(3);
        } else if (Number.isInteger(num)) {
          return num.toString();
        } else {
          return num.toFixed(4);
        }
      }
      
      return String(value);
    },
    
    formatVariableName(varName) {
      if (!varName) return ''
      const name = varName.replace(/^(meta_|numerical_|survival_)/, '')
      return name.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
    },

    getVariableTag(varName) {
      const tags = {
        meta_: { label: 'Categorical', color: '#409EFF', bgColor: '#ecf5ff' },
        numerical_: { label: 'Continuous', color: '#67C23A', bgColor: '#f0f9eb' },
        survival_: { label: 'Survival', color: '#E6A23C', bgColor: '#fdf6ec' }
      }
      const prefix = Object.keys(tags).find(p => varName?.startsWith(p))
      return prefix ? tags[prefix] : { label: 'Other', color: '#909399', bgColor: '#f4f4f5' }
    },

    getVariableTagStyle(varName) {
      const tag = this.getVariableTag(varName)
      return { color: tag.color, backgroundColor: tag.bgColor, borderColor: tag.color }
    },

    applyNetworkThreshold() {
      const raw = this.correlationNetworkRaw || { nodes: [], links: [], categories: [] }
      const thr = Number(this.clinicalParams.networkAbsRThreshold || 0)
      if (!raw.links || raw.links.length === 0) {
        this.correlationNetworkData = { nodes: raw.nodes || [], links: [], categories: raw.categories || [] }
        return
      }
      const links = raw.links.filter(l => {
        const v = Number(l && l.value)
        return isFinite(v) && Math.abs(v) >= thr
      })
      const used = new Set()
      for (const l of links) { used.add(String(l.source)); used.add(String(l.target)); }
      const nodes = (raw.nodes || []).filter(n => used.has(String(n.id)))
      this.correlationNetworkData = {
        nodes,
        links,
        categories: raw.categories || []
      }
    },
    onChangeEnrichmentXAxis() {
      try {
        // Re-run enrichment with new sort order
        // The x-axis type will be automatically updated via prop binding
        this.runEnrichment()
      } catch (e) { /* no-op */ }
    },
    exportEnrichmentPng() {
      try {
        if (!this.$refs || !this.$refs.enrichmentPlotRef) return
        this.$refs.enrichmentPlotRef.openSaveDialog()
      } catch (e) { /* no-op */ }
    },
    downloadEnrichmentTable() {
      this.exportEnrichmentToCSV()
    },
    swapMarkerGroupAndSubgroup() {
      try {
        const g = this.markerParams && this.markerParams.groupBy
        const sg = this.markerParams && this.markerParams.subgroupBy
        if (!g || !sg) return
        this.markerParams.groupBy = sg
        this.markerParams.subgroupBy = g
        this.runMarkerBoxplots()
      } catch (e) { /* no-op */ }
    },
    // Rerun active module when obsFilter changes
    onObsFilterChanged() {
      // Fetch filtered obs_variables first
      this.fetchFilteredObsVariables()
      
      const mod = this.selectedModule
      // Do NOT auto-run differential expression on filter change; user triggers via button
      if (mod === 'markerGenes') this.runMarkerBoxplots()
      else if (mod === 'clinicalCorrelation') { this.runClinicalCorrelation(); this.runClinicalHeatmap(); this.runCorrelationNetwork(); }
      // Survival analysis requires manual trigger via "Run Survival Analysis" button
    },
    fetchFilteredObsVariables() {
      // Fetch filtered obs_variables based on current obsFilter
      axios.post('/api/projectDetail/get_filtered_obs_variables', {
        datasetid: this.id,
        obs_filter: this.obsFilter
      }).then(response => {
        if (response.data.code === 200) {
          this.filteredObsVariables = response.data.data
        } else {
          // On error, fall back to unfiltered
          this.filteredObsVariables = this.project ? this.project.obs_variables : []
        }
      }).catch(() => {
        // On error, fall back to unfiltered
        this.filteredObsVariables = this.project ? this.project.obs_variables : []
      })
    },
    async runCorrelationNetwork() {
      try {
        if (
          this.selectedModule !== 'clinicalCorrelation' ||
          !Array.isArray(this.clinicalParams.selectedGenes) ||
          this.clinicalParams.selectedGenes.length === 0
        ) {
          this.correlationNetworkData = { nodes: [], links: [], categories: [] }
          this.correlationNetworkRaw = { nodes: [], links: [], categories: [] }
          return
        }
        const resp = await axios.post('/api/projectDetail/get_correlation_network', {
          datasetid: this.id,
          genes: this.clinicalParams.selectedGenes,
          method: String(this.clinicalParams.correlationMethod || 'pearson').toLowerCase(),
          // fetch full network once; apply threshold client-side
          r_threshold: 0,
          obs_filter: this.obsFilter
        })
        const data = (resp && resp.data && resp.data.data) || {}
        this.correlationNetworkRaw = {
          nodes: data.nodes || [],
          links: data.links || [],
          categories: data.categories || []
        }
        this.applyNetworkThreshold()
      } catch (e) {
        this.correlationNetworkRaw = { nodes: [], links: [], categories: [] }
        this.correlationNetworkData = { nodes: [], links: [], categories: [] }
      }
    },
    async copyGenes(target) {
      const genes = (() => {
        if (target === 'marker') return this.markerParams.selectedGenes
        if (target === 'clinical') return this.clinicalParams.selectedGenes
        if (target === 'function') return this.functionParams.selectedGenes
        if (target === 'survival') return this.survivalParams.selectedGenes
        if (target === 'enrichment') return this.enrichmentParams.selectedGenes
        return []
      })()
      const list = Array.isArray(genes) ? genes : []
      const text = list.join(', ')
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        this.$notify({
          title: 'Success',
          message: 'Copied proteins to clipboard',
          type: 'success',
          position: 'top-right'
        })
      } catch (err) {
        try {
          const ta = document.createElement('textarea')
          ta.value = text
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
          this.$notify({
          title: 'Success',
          message: 'Copied proteins to clipboard',
          type: 'success',
          position: 'top-right'
        })
        } catch (e) {
          this.$notify({
            title: 'Error',
            message: 'Failed to copy proteins',
            type: 'error',
            position: 'top-right'
          })
        }
      }
    },
    handleGenePaste(e, target) {
      try {
        const clipboard = (e && e.clipboardData && e.clipboardData.getData) ? e.clipboardData.getData('text') : ''
        if (!clipboard) return
        e.preventDefault()
        const parsed = clipboard
          .split(/[\n,;\t ]+/)
          .map(s => (s || '').trim())
          .filter(Boolean)
        if (!parsed.length) return
        const unique = Array.from(new Set(parsed))
        const mergeUnique = (existing, incoming) => Array.from(new Set([...(Array.isArray(existing) ? existing : []), ...incoming]))
        if (target === 'marker') {
          this.$set(this.markerParams, 'selectedGenes', mergeUnique(this.markerParams.selectedGenes, unique))
          this.runMarkerBoxplots()
        } else if (target === 'clinical') {
          this.$set(this.clinicalParams, 'selectedGenes', mergeUnique(this.clinicalParams.selectedGenes, unique))
        } else if (target === 'function') {
          this.$set(this.functionParams, 'selectedGenes', mergeUnique(this.functionParams.selectedGenes, unique))
        } else if (target === 'survival') {
          this.$set(this.survivalParams, 'selectedGenes', mergeUnique(this.survivalParams.selectedGenes, unique))
        } else if (target === 'enrichment') {
          this.$set(this.enrichmentParams, 'selectedGenes', mergeUnique(this.enrichmentParams.selectedGenes, unique))
        }
      } catch (err) {
        // no-op
      }
    },
    formatStatValue(value) {
      try {
        if (value === null || value === undefined) return ''
        if (typeof value === 'boolean') return value ? 'True' : 'False'
        if (typeof value === 'number') return value.toLocaleString()
        if (typeof value === 'string') {
          const num = Number(value)
          if (!isNaN(num)) return num.toLocaleString()
          return value
        }
        return String(value)
      } catch (e) {
        return String(value)
      }
    },
    // New method to run both survival analyses together
    async runSurvivalAnalysis() {
      if (this.selectedModule !== 'survivalAnalysis') return
      const genes = Array.isArray(this.survivalParams.selectedGenes) ? this.survivalParams.selectedGenes : []
      if (genes.length === 0) {
        this.$notify({
          title: 'Warning',
          message: 'Please select at least one protein',
          type: 'warning',
          position: 'top-right'
        })
        return
      }
      
      // Set loading state for both
      this.survivalResults.loading = true
      
      // Run both analyses in parallel
      try {
        await Promise.all([
          this.runSurvival(),
          this.runCoxForest()
        ])
      } finally {
        this.survivalResults.loading = false
      }
    },
    async runSurvival() {
      try {
        if (this.selectedModule !== 'survivalAnalysis') return
        const genes = Array.isArray(this.survivalParams.selectedGenes) ? this.survivalParams.selectedGenes : []
        if (genes.length === 0) {
          this.survivalResults.plots = []
          this.survivalResults.missing = []
          return
        }
        const reqId = ++this.survivalResults.kmReqId
        
        // Call the endpoint for individual gene survival plots
        const payload = {
          datasetid: this.id,
          genes,
          cutoff_method: String(this.survivalParams.cutoffMethod || 'q2').toLowerCase(),
          survival_type: String(this.survivalParams.survivalType || 'OS').toUpperCase(),
          time_unit: String(this.survivalParams.survivalUnit || 'month').toLowerCase(),
          hide_insignificant: Boolean(this.survivalParams.hideInsignificantGenes)
        }
        payload.obs_filter = this.obsFilter
        
        const resp = await axios.post('/api/projectDetail/get_kaplan_meier', payload)
        if (reqId === this.survivalResults.kmReqId) {
          const data = (resp && resp.data && resp.data.data) || {}
          this.survivalResults.plots = Array.isArray(data.plots) ? data.plots : []
          this.survivalResults.missing = Array.isArray(data.missing_genes) ? data.missing_genes : []
          
          // Reset pagination on new results
          if (this.survivalParams && this.survivalParams.pagination) {
            this.survivalParams.pagination.pageNum = 1
          }
        }
      } catch (e) {
        this.survivalResults.plots = []
        this.survivalResults.missing = []
      }
    },
    async runCoxForest() {
      try {
        if (this.selectedModule !== 'survivalAnalysis') return
        const genes = Array.isArray(this.survivalParams.selectedGenes) ? this.survivalParams.selectedGenes : []
        if (genes.length === 0) {
          this.survivalResults.coxPlot = { data: [], layout: {}, config: {} }
          return
        }
        const reqId = ++this.survivalResults.coxReqId
        const payload = {
          datasetid: this.id,
          genes,
          cutoff_method: String(this.survivalParams.cutoffMethod || 'q2').toLowerCase(),
          survival_type: String(this.survivalParams.survivalType || 'OS').toUpperCase(),
          time_unit: String(this.survivalParams.survivalUnit || 'month').toLowerCase(),
          clinical_variables: Array.isArray(this.survivalParams.clinicalVariables) ? this.survivalParams.clinicalVariables : [],
          hide_insignificant: Boolean(this.survivalParams.hideInsignificantGenes)
        }
        payload.obs_filter = this.obsFilter
        const resp = await axios.post('/api/projectDetail/get_cox_forest', payload)
        if (reqId === this.survivalResults.coxReqId) {
          const data = (resp && resp.data && resp.data.data) || {}
          this.survivalResults.coxPlot = data.coxPlot || { data: [], layout: {}, config: {} }
        }
      } catch (e) {
        this.survivalResults.coxPlot = { data: [], layout: {}, config: {} }
      }
    },
    isPlotEmpty(data) {
      if (!data) return true
      if (Array.isArray(data) && data.length === 0) return true
      if (Array.isArray(data)) {
        // Consider empty if all traces have no x/y values
        const hasPoints = data.some(trace => {
          const x = trace && trace.x
          const y = trace && trace.y
          const z = trace && trace.z
          const valuesLength = (arr) => Array.isArray(arr) && arr.filter(v => v != null).length
          return valuesLength(x) > 0 || valuesLength(y) > 0 || valuesLength(z) > 0
        })
        return !hasPoints
      }
      return false
    },
    openGeneFunctionDetails(row) {
      this.geneFunctionDetails.row = row
      this.geneFunctionDetails.visible = true
    },
    async copyGeneFunctionRow(row) {
      if (!row) return
      const columns = [
        { key: 'gene_name', label: 'Gene name' },
        { key: 'protein_accession', label: 'Protein accession' },
        { key: 'protein_description', label: 'Protein description' },
        { key: 'kog_category', label: 'KOG category' },
        { key: 'kog_no', label: 'KOG NO.' },
        { key: 'kog_description', label: 'KOG description' },
        { key: 'biological_process', label: 'Biological Process' },
        { key: 'cellular_component', label: 'Cellular Component' },
        { key: 'molecular_function', label: 'Molecular Function' },
        { key: 'pfam_id', label: 'Pfam ID' },
        { key: 'domain_description', label: 'Domain description' },
        { key: 'subcellular_localization', label: 'Subcellular localization' },
        { key: 'kegg_ko_no', label: 'KEGG KO No.' },
        { key: 'kegg_gene', label: 'KEGG Gene' },
        { key: 'kegg_pathway', label: 'KEGG pathway' },
        { key: 'hallmark', label: 'HallMark' },
        { key: 'msigdburl', label: 'msigdbURL' },
        { key: 'reactome_pathway', label: 'Reactome pathway' },
        { key: 'reactome_link', label: 'Reactome link' },
        { key: 'wikipathways', label: 'WikiPathways' },
        { key: 'externaldetailsurl', label: 'externalDetailsURL' },
        { key: 'is_tf', label: 'IS_TF' }
      ]
      const header = columns.map(c => c.label).join('\t')
      const values = columns.map(c => {
        const val = row[c.key]
        if (val == null) return ''
        // Replace newlines and tabs to keep TSV intact
        return String(val).replace(/\t/g, ' ').replace(/\r?\n/g, ' ')
      }).join('\t')
      const text = `${header}\n${values}`
      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          // Fallback for older browsers
          const ta = document.createElement('textarea')
          ta.value = text
          ta.setAttribute('readonly', '')
          ta.style.position = 'absolute'
          ta.style.left = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.$notify({
          title: 'Success',
          message: 'Row copied to clipboard',
          type: 'success',
          position: 'top-right'
        })
      } catch (e) {
        this.$notify({
          title: 'Error',
          message: 'Failed to copy row',
          type: 'error',
          position: 'top-right'
        })
      }
    },
    async runGeneFunction() {
      try {
        if (this.selectedModule !== 'geneFunction') {
          this.functionResults.table = []
          if (this.functionResults && this.functionResults.pagination) {
            this.functionResults.pagination.total = 0
            this.functionResults.pagination.pageNum = 1
          }
          return
        }
        this.functionResults.loading = true
        let payload = {}
        if (this.functionParams.searchMode === 'text') {
          const query = String(this.functionParams.textQuery || '').trim()
          if (!query) {
            this.functionResults.table = []
            if (this.functionResults && this.functionResults.pagination) {
              this.functionResults.pagination.total = 0
              this.functionResults.pagination.pageNum = 1
            }
            return
          }
          payload = { mode: 'text', query }
        } else {
          const genes = Array.from(new Set(this.functionParams.selectedGenes))
          if (!genes.length) {
            this.functionResults.table = []
            if (this.functionResults && this.functionResults.pagination) {
              this.functionResults.pagination.total = 0
              this.functionResults.pagination.pageNum = 1
            }
            return
          }
          payload = { mode: 'genes', genes }
        }
        const resp = await axios.post('/api/projectDetail/get_gene_function', payload)
        const items = (resp && resp.data && resp.data.data) || []
        this.functionResults.table = items
        if (this.functionResults && this.functionResults.pagination) {
          this.functionResults.pagination.total = items.length
          this.functionResults.pagination.pageNum = 1
        }
      } catch (e) {
        this.functionResults.table = []
        if (this.functionResults && this.functionResults.pagination) {
          this.functionResults.pagination.total = 0
          this.functionResults.pagination.pageNum = 1
        }
      } finally {
        this.functionResults.loading = false
      }
    },
    onFunctionModeChange() {
      // Clear the other mode inputs and rerun
      if (this.functionParams.searchMode === 'text') {
        this.$set(this.functionParams, 'selectedGenes', [])
      } else {
        this.$set(this.functionParams, 'textQuery', '')
      }
      this.runGeneFunction()
    },
    onFunctionPageChange(page) {
      if (!this.functionResults || !this.functionResults.pagination) return
      this.functionResults.pagination.pageNum = page
    },
    onFunctionPageSizeChange(size) {
      if (!this.functionResults || !this.functionResults.pagination) return
      this.functionResults.pagination.pageSize = size
      this.functionResults.pagination.pageNum = 1
    },
    getEnrichmentGeneList() {
      return Array.isArray(this.enrichmentParams.selectedGenes) ? this.enrichmentParams.selectedGenes : []
    },
    parsePathwayDatabase() {
      // Parse combined pathwayDatabase value into database and goCategory
      const value = this.enrichmentParams.pathwayDatabase || 'go-bp'
      if (value.startsWith('go-')) {
        return {
          database: 'go',
          go_category: value.substring(3).toUpperCase() // 'go-bp' -> 'BP'
        }
      } else if (value === 'msigdb-hallmark') {
        return {
          database: 'msigdb hallmark',
          go_category: null
        }
      } else {
        return {
          database: value,
          go_category: null
        }
      }
    },
    async runEnrichment() {
      try {
        if (this.selectedModule !== 'functionalEnrichment') return
        const genes = Array.from(new Set(this.getEnrichmentGeneList()))
        if (!genes.length) {
          this.enrichmentResults.bubblePlot = { data: [], layout: {}, config: {} }
          this.enrichmentResults.echartsPlot = { pathways: [], data: [], legendCircles: { radii: [], labels: [] } }
          this.enrichmentResults.table = []
          return
        }
        this.enrichmentResults.loading = true
        
        // Parse combined pathwayDatabase value
        const { database, go_category } = this.parsePathwayDatabase()
        
        const resp = await axios.post('/api/projectDetail/get_enrichment', {
          genes,
          p_cutoff: this.enrichmentParams.pValueCutoff,
          database: database,
          go_category: go_category,
          min_gene_ratio: this.enrichmentParams.geneRatioMin,
          min_overlap_ratio: this.enrichmentParams.overlapRatioMin,
          max_terms: this.enrichmentParams.topN,
          sort_by: this.enrichmentXAxis === 'oddsRatio' ? 'odds_ratio' : 'gene_ratio'
        })
        const bubble = (resp && resp.data && resp.data.data && resp.data.data.bubblePlot) || { data: [], layout: {}, config: {} }
        const echarts = (resp && resp.data && resp.data.data && resp.data.data.echartsPlot) || { pathways: [], data: [], legendCircles: { radii: [], labels: [] } }
        const table = (resp && resp.data && resp.data.data && resp.data.data.table) || []
        this.enrichmentResults.bubblePlot = bubble
        this.enrichmentResults.echartsPlot = echarts
        this.enrichmentResults.table = table
      } catch (e) {
        this.enrichmentResults.bubblePlot = { data: [], layout: {}, config: {} }
        this.enrichmentResults.echartsPlot = { pathways: [], data: [], legendCircles: { radii: [], labels: [] } }
        this.enrichmentResults.table = []
      } finally {
        this.enrichmentResults.loading = false
      }
    },
    onEnrichmentPlotSaved() {
      // Refresh enrichment data after saving the plot
      this.runEnrichment()
    },
    async fetchEnrichmentGenes(query) {
      try {
        this.enrichmentParams.loading = true
        await this.fetchGeneList(query, 'enrichmentParams')
      } finally {
        this.enrichmentParams.loading = false
      }
    },
    exportEnrichmentToCSV() {
      if (!this.enrichmentResults.table || this.enrichmentResults.table.length === 0) {
        this.$notify({
          title: 'Warning',
          message: 'No data to export',
          type: 'warning',
          position: 'top-right'
        })
        return
      }
      
      // Define headers
      const headers = [
        'Source',
        'Term',
        'Overlap',
        'Term Size',
        'Gene Ratio',
        'Odds Ratio',
        'P-value',
        'FDR',
        'Overlap Genes'
      ]
      
      // Helper function to escape CSV values
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return ''
        const str = String(value)
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return '"' + str.replace(/"/g, '""') + '"'
        }
        return str
      }
      
      // Build CSV content
      let csvContent = headers.join(',') + '\n'
      
      this.enrichmentResults.table.forEach(row => {
        const rowData = [
          escapeCSV(row.source),
          escapeCSV(row.term),
          row.overlap || '',
          row.n_term_genes || '',
          row.gene_ratio ? Number(row.gene_ratio).toFixed(3) : '',
          row.odds_ratio ? Number(row.odds_ratio).toFixed(2) : '',
          row.pval || '',
          row.fdr || '',
          escapeCSV(row.overlap_genes)
        ]
        csvContent += rowData.join(',') + '\n'
      })
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `enrichment_results_${new Date().getTime()}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      this.$notify({
        title: 'Success',
        message: 'CSV exported successfully',
        type: 'success',
        position: 'top-right'
      })
    },
    // ===== Molecular Subtyping Methods =====
    async loadAvailableAnnotations() {
      try {
        this.subtypeParams.loadingAnnotations = true;
        const resp = await axios.post('/api/projectDetail/get_available_annotations', {
          datasetid: this.id,
          obs_filter: this.obsFilter
        });
        const data = (resp && resp.data && resp.data.data) || {};
        const columns = Array.isArray(data.columns) ? data.columns : [];
        
        this.availableAnnotations = columns;
        
        // All meta_ columns are available for subtype selection
        this.availableSubtypeColumns = columns;
        
        // Auto-select meta_subtype if available
        if (!this.subtypeParams.subtypeCol && columns.some(c => c.name === 'meta_subtype')) {
          this.subtypeParams.subtypeCol = 'meta_subtype';
        }
        
        // Auto-populate annoVars if empty and subtypeCol is set
        if (this.subtypeParams.subtypeCol && (!this.subtypeParams.annoVars || this.subtypeParams.annoVars.length === 0)) {
          // Default annotations: subtypeCol first, then some common ones
          const defaultAnnos = [this.subtypeParams.subtypeCol];
          const commonAnnos = ['meta_stage_tnm', 'meta_isup_grade', 'survival_progression', 'meta_T', 'meta_N', 'meta_M', 'meta_sex'];
          for (const anno of commonAnnos) {
            if (columns.some(c => c.name === anno) && !defaultAnnos.includes(anno)) {
              defaultAnnos.push(anno);
            }
            if (defaultAnnos.length >= 8) break;
          }
          this.subtypeParams.annoVars = defaultAnnos;
        }
      } catch (e) {
        console.error('Error loading annotations:', e);
        this.availableAnnotations = [];
        this.availableSubtypeColumns = [];
      } finally {
        this.subtypeParams.loadingAnnotations = false;
      }
    },
    async runSubtypingHeatmap() {
      if (!this.subtypeParams.subtypeCol) {
        this.$notify({
          title: 'Warning',
          message: 'Please select a subtype column',
          type: 'warning',
          position: 'top-right'
        });
        return;
      }
      
      try {
        this.subtypeResults.loading = true;
        
        const genes = (this.subtypeParams.selectedGenes && this.subtypeParams.selectedGenes.length > 0) 
          ? this.subtypeParams.selectedGenes 
          : null;
        
        // Ensure subtype column is always the first annotation row
        let annoVars = [...(this.subtypeParams.annoVars || [])];
        if (!annoVars.includes(this.subtypeParams.subtypeCol)) {
          annoVars.unshift(this.subtypeParams.subtypeCol);
        } else {
          // Move subtype column to first position
          annoVars = annoVars.filter(v => v !== this.subtypeParams.subtypeCol);
          annoVars.unshift(this.subtypeParams.subtypeCol);
        }
        
        const resp = await axios.post('/api/projectDetail/get_subtyping_heatmap', {
          datasetid: this.id,
          anno_vars: annoVars,
          subtype_col: this.subtypeParams.subtypeCol,
          genes: genes,
          top_n_genes: this.subtypeParams.topNGenes,
          // Default-on: order genes to emphasize subtype blocks (group1-high genes first).
          rank_by_subtype: true,
          zmin: -this.subtypeParams.zmax,
          zmax: this.subtypeParams.zmax,
          obs_filter: this.obsFilter
        });
        
        const data = (resp && resp.data && resp.data.data) || {};
        
        this.subtypeResults.heatmap = data.heatmap || { data: [], layout: {}, config: {} };
        this.subtypeResults.subtype_order = data.subtype_order || [];
        this.subtypeResults.subtype_colors = data.subtype_colors || {};
        this.subtypeResults.n_samples = data.n_samples || 0;
        this.subtypeResults.n_genes = data.n_genes || 0;
        this.subtypeResults.gene_list = data.gene_list || [];
        
        if (this.subtypeResults.n_samples === 0) {
          this.$notify({
            title: 'Warning',
            message: 'No samples found for the current filter',
            type: 'warning',
            position: 'top-right'
          });
        }
      } catch (e) {
        console.error('Error generating subtyping heatmap:', e);
        this.subtypeResults.heatmap = { data: [], layout: {}, config: {} };
        this.$notify({
          title: 'Error',
          message: e.response?.data?.error || 'Failed to generate heatmap',
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.subtypeResults.loading = false;
      }
    },
    async copySubtypeGenes() {
      try {
        const genes = this.subtypeParams.selectedGenes || [];
        if (genes.length === 0) return;
        const text = genes.join(', ');
        await navigator.clipboard.writeText(text);
        this.$notify({
          title: 'Success',
          message: `Copied ${genes.length} genes to clipboard`,
          type: 'success',
          position: 'top-right'
        });
      } catch (err) {
        console.error('Failed to copy genes:', err);
      }
    },
    handleSubtypeGenePaste(e) {
      try {
        const text = (e && e.clipboardData && e.clipboardData.getData) ? e.clipboardData.getData('text') : '';
        if (!text) return;
        e.preventDefault();
        const parsed = text.split(/[\n,;\t ]+/).map(s => (s || '').trim()).filter(Boolean);
        if (!parsed.length) return;
        const merged = Array.from(new Set([...(this.subtypeParams.selectedGenes || []), ...parsed]));
        this.subtypeParams.selectedGenes = merged;
      } catch (err) {
        // no-op
      }
    },
    async runClinicalCorrelation() {
      try {
        if (
          this.selectedModule !== 'clinicalCorrelation' ||
          !Array.isArray(this.clinicalParams.selectedGenes) ||
          this.clinicalParams.selectedGenes.length === 0 ||
          !this.clinicalParams.clinicalVariable
        ) {
          // Clear plots if inputs are incomplete
          this.clinicalResults.scatterPlot = { data: [], layout: {}, config: {} };
          this.clinicalResults.correlationStats = null;
          return;
        }

        const resp = await axios.post('/api/projectDetail/get_clinical_correlation', {
          datasetid: this.id,
          genes: this.clinicalParams.selectedGenes,
          clinical_variable: this.clinicalParams.clinicalVariable,
          method: String(this.clinicalParams.correlationMethod || 'pearson').toLowerCase(),
          obs_filter: this.obsFilter
        });
        const data = (resp && resp.data && resp.data.data) || {};
        const scatter = data.scatterPlot || { data: [], layout: {}, config: {} };
        this.clinicalResults.scatterPlot = scatter;
        this.clinicalResults.correlationStats = data.correlation || null;
      } catch (e) {
        this.clinicalResults.scatterPlot = { data: [], layout: {}, config: {} };
        this.clinicalResults.correlationStats = null;
      }
    },
    async runClinicalHeatmap() {
      try {
        if (
          this.selectedModule !== 'clinicalCorrelation' ||
          !Array.isArray(this.clinicalParams.selectedGenes) ||
          this.clinicalParams.selectedGenes.length === 0
        ) {
          this.clinicalResults.heatmap = { data: [], layout: {}, config: {} };
          this.$set(this.clinicalResults, 'rows', []);
          this.$set(this.clinicalResults, 'cols', []);
          return;
        }
        const resp = await axios.post('/api/projectDetail/get_correlation_heatmap', {
          datasetid: this.id,
          genes: this.clinicalParams.selectedGenes,
          method: String(this.clinicalParams.correlationMethod || 'pearson').toLowerCase(),
          obs_filter: this.obsFilter
        });
        const data = (resp && resp.data && resp.data.data) || {};
        const heatmap = data.heatmap || { data: [], layout: {}, config: {} };
        this.clinicalResults.heatmap = heatmap;
        // Store row/column labels for layout sizing
        this.$set(this.clinicalResults, 'rows', Array.isArray(data.rows) ? data.rows : []);
        this.$set(this.clinicalResults, 'cols', Array.isArray(data.cols) ? data.cols : []);
      } catch (e) {
        this.clinicalResults.heatmap = { data: [], layout: {}, config: {} };
        this.$set(this.clinicalResults, 'rows', []);
        this.$set(this.clinicalResults, 'cols', []);
      }
    },
    useGenesForFollowing() {
      if (!Array.isArray(this.deParams.geneInterestList) || this.deParams.geneInterestList.length === 0) {
        return;
      }
      const uniqueGenes = Array.from(new Set(this.deParams.geneInterestList));
      if (!this.globalParams) {
        this.$set(this, 'globalParams', { geneInterestList: [] });
      }
      this.$set(this.globalParams, 'geneInterestList', uniqueGenes);
      // Use the unified selectedGenes for all modules
      this.$set(this.clinicalParams, 'selectedGenes', uniqueGenes);
      this.$set(this.survivalParams, 'selectedGenes', uniqueGenes);
      this.$set(this.functionParams, 'selectedGenes', uniqueGenes);
      this.$set(this.markerParams, 'selectedGenes', uniqueGenes);
      this.$set(this.enrichmentParams, 'selectedGenes', uniqueGenes);
      // Ensure marker module follows the current DE grouping
      if (!this.markerParams.groupBy && this.deParams.groupby) {
        this.markerParams.groupBy = this.deParams.groupby;
      }
      // Switch to next module (Marker Proteins) and auto-run boxplots
      this.$nextTick(() => {
        this.selectedModule = 'markerGenes';
        if ((this.markerParams.selectedGenes || []).length && this.markerParams.groupBy) {
          this.runMarkerBoxplots();
        }
      });
    },
    async fetchGeneList(query, module) {
      try {
        if (!query || String(query).trim() === '') {
          // Show default suggestions when no keyword is provided
          if (this[module]) this[module].gene_list = this.project.geneSymbols.slice(0, 100);
          return;
        }
        const payload = { datasetid: this.id, search: query };
        const response = await axios.post('/api/projectDetail/search_genes', payload);
        const list = response && response.data && response.data.data && response.data.data.content && response.data.data.content.search_list;
        if (this[module]) this[module].gene_list = Array.isArray(list) ? list : [];
      } catch (error) {
        console.error('Error fetching gene options:', error);
        if (this[module]) this[module].gene_list = [];
      }
    },
    async fetchMarkerGenes(query) {
      try {
        this.markerParams.loading = true;
        await this.fetchGeneList(query, 'markerParams');
      } finally {
        this.markerParams.loading = false;
      }
    },
    async runMarkerBoxplots() {
      this.markerResults.loading = true;
      try {
        const payload = {
          datasetid: this.id,
          genes: this.markerParams.selectedGenes,
          groupby: this.markerParams.groupBy,
          normalize: this.markerParams.normalize,
          show_jitter: this.markerParams.showJitter
        };
        if (this.markerParams.subgroupBy) {
          payload.subgroupby = this.markerParams.subgroupBy;
        }
        payload.obs_filter = this.obsFilter;
        const resp = await axios.post('/api/projectDetail/get_gene_boxplots', payload);
        const data = (resp && resp.data && resp.data.data) || {};
        this.markerResults.plots = Array.isArray(data.plots) ? data.plots : [];
        // Support multiple possible keys from backend for averaged plot
        this.markerResults.avgPlot = (data.avg_plot || data.average_plot || data.avgPlot) || null;
        this.markerResults.missing = Array.isArray(data.missing_genes) ? data.missing_genes : [];
        // Reset pagination on new results
        if (this.markerParams && this.markerParams.pagination) {
          this.markerParams.pagination.pageNum = 1;
        }
      } catch (e) {
        this.markerResults.plots = [];
        this.markerResults.avgPlot = null;
        this.markerResults.missing = [];
      } finally {
        this.markerResults.loading = false;
      }
    },
    async runPCA() {
      if (!this.pcaParams.groupBy) {
        this.$message.warning('Please select a sample grouping variable');
        return;
      }
      
      this.pcaResults.loading = true;
      try {
        const payload = {
          datasetid: this.id,
          group_by: this.pcaParams.groupBy,
          obs_filter: this.obsFilter
        };
        const resp = await axios.post('/api/projectDetail/get_pca', payload);
        const data = (resp && resp.data && resp.data.data) || {};
        this.pcaResults.data = data.plotly_data || [];
        this.pcaResults.layout = data.plotly_layout || {};
        this.pcaResults.config = data.plotly_config || {};
      } catch (e) {
        this.$message.error(e.response?.data?.error || 'Failed to run PCA analysis');
        this.pcaResults.data = [];
        this.pcaResults.layout = {};
        this.pcaResults.config = {};
      } finally {
        this.pcaResults.loading = false;
      }
    },
    handleMarkerPageChange(page) {
      if (!this.markerParams || !this.markerParams.pagination) return;
      this.markerParams.pagination.pageNum = page;
    },
    handleMarkerPageSizeChange(size) {
      if (!this.markerParams || !this.markerParams.pagination) return;
      this.markerParams.pagination.pageSize = size;
      this.markerParams.pagination.pageNum = 1;
    },
    handleMarkerSearchChange() {
      if (!this.markerParams || !this.markerParams.pagination) return;
      this.markerParams.pagination.pageNum = 1;
    },
    handleSurvivalPageChange(page) {
      if (!this.survivalParams || !this.survivalParams.pagination) return;
      this.survivalParams.pagination.pageNum = page;
    },
    handleSurvivalPageSizeChange(size) {
      if (!this.survivalParams || !this.survivalParams.pagination) return;
      this.survivalParams.pagination.pageSize = size;
      this.survivalParams.pagination.pageNum = 1;
    },
    handleSurvivalSearchChange() {
      if (!this.survivalParams || !this.survivalParams.pagination) return;
      this.survivalParams.pagination.pageNum = 1;
    },
    async fetchGeneFunction(query) {
      try {
        this.functionParams.loading = true;
        await this.fetchGeneList(query, 'functionParams');
      } finally {
        this.functionParams.loading = false;
      }
    },
    async fetchClinicalGenes(query) {
      try {
        this.clinicalParams.loading = true;
        await this.fetchGeneList(query, 'clinicalParams');
      } finally {
        this.clinicalParams.loading = false;
      }
    },
    async fetchSurvivalGenes(query) {
      try {
        this.survivalParams.loading = true;
        await this.fetchGeneList(query, 'survivalParams');
      } finally {
        this.survivalParams.loading = false;
      }
    },
    handleTableSelection(selectedGenes) {
      // Set genes of interest to selected genes from table
      if (!Array.isArray(this.deParams.geneInterestList)) {
        this.$set(this.deParams, 'geneInterestList', []);
      }
      
      // Set the selection to match table selection
      this.$set(this.deParams, 'geneInterestList', selectedGenes);
    },
    handleRemoveGene(gene) {
      if (!Array.isArray(this.deParams.geneInterestList)) return;
      this.deParams.geneInterestList = this.deParams.geneInterestList.filter(g => g !== gene);
    },
    selectAllDeGenes() {
      try {
        const rows = this.deFilteredData || [];
        if (rows.length === 0) return;
        const allGenes = rows.map(r => r && (r.geneSymbol || r.gene || r.symbol)).filter(Boolean);
        const uniqueGenes = Array.from(new Set(allGenes));
        // Clear current selection and set to all genes
        this.$set(this.deParams, 'geneInterestList', uniqueGenes);
      } catch (e) { console.error("selectAllDeGenes error:", e); }
    },
    clearAllDeGenes() {
      try {
        if (!Array.isArray(this.deParams.geneInterestList) || this.deParams.geneInterestList.length === 0) return;
        this.$set(this.deParams, 'geneInterestList', []);
      } catch (e) { console.error("clearAllDeGenes error:", e); }
    },
    isGeneInInterest(gene) {
      return Array.isArray(this.deParams.geneInterestList) && this.deParams.geneInterestList.includes(gene);
    },
    getProjectDetail() {
      axios.post('/api/projectDetail/get_project_detail', {
        datasetid: this.id
      }).then(response => {
        if (response.data.code === 200) {
          this.project = response.data.data;
          
          // Update browser tab title
          if (this.project && this.project.metadata && this.project.metadata.datasetid) {
            document.title = `${this.project.metadata.datasetid} - 景杰生物数据平台`;
          }
          
          // Initialize filtered obs_variables with unfiltered data
          this.filteredObsVariables = this.project.obs_variables || []
          
          // Handle slice data availability
          if (response.data.data.has_slice_data) {
            this.sliceMagnifications = response.data.data.slice_magnifications || [];
            // Make sliceDisplay module visible
            const sliceModule = this.modules.find(m => m.name === 'sliceDisplay');
            if (sliceModule) {
              sliceModule.show = true;
            }
          }
          
          // Check if molecular subtyping is available (has any meta_ columns)
          const obsVars = this.project.obs_variables || [];
          const hasMetaColumns = obsVars.some(v => v.name.startsWith('meta_'));
          if (hasMetaColumns) {
            const subtypingModule = this.modules.find(m => m.name === 'molecularSubtyping');
            if (subtypingModule) {
              subtypingModule.show = true;
              // Load available annotations for subtyping
              this.$nextTick(() => {
                this.loadAvailableAnnotations();
              });
            }
          }
          
          // Auto-load first variable for overview plot
          this.$nextTick(() => {
            if (this.selectedModule === 'datasetIntroduction' && this.selectableObsVariables && this.selectableObsVariables.length > 0) {
              this.overviewParams.selectedVariable = this.selectableObsVariables[0].name;
              this.loadOverviewDistribution();
            }
            // Auto-select first variable for PCA
            if (this.selectableObsVariables && this.selectableObsVariables.length > 0 && !this.pcaParams.groupBy) {
              this.pcaParams.groupBy = this.selectableObsVariables[0].name;
            }
          });
        } else {
          this.$notify({
            title: 'Error',
            message: response.data.error || 'Failed to load project',
            type: 'error',
            position: 'top-right'
          });
          this.$router.push('/projects');
        }
      }).catch(error => {
        this.$notify({
          title: 'Error',
          message: 'Failed to load project: ' + error.message,
          type: 'error',
          position: 'top-right'
        });
        this.$router.push('/projects');
      });
    },
    async loadOverviewDistribution() {
      if (!this.overviewParams.selectedVariable) {
        this.overviewPlot = {
          data: [],
          layout: {},
          config: {}
        };
        return;
      }
      
      this.overviewParams.loading = true;
      try {
        const response = await axios.post('/api/projectDetail/get_obs_variable_distribution', {
          datasetid: this.id,
          variable_name: this.overviewParams.selectedVariable,
          obs_filter: this.obsFilter
        });
        
        if (response.data.code === 200) {
          const plotData = response.data.data.plot;
          this.overviewPlot = {
            data: plotData.data || [],
            layout: plotData.layout || {},
            config: plotData.config || {}
          };
        } else {
          this.$notify({
            title: 'Error',
            message: response.data.error || 'Failed to load distribution',
            type: 'error',
            position: 'top-right'
          });
          this.overviewPlot = {
            data: [],
            layout: {},
            config: {}
          };
        }
      } catch (error) {
        this.$notify({
          title: 'Error',
          message: 'Failed to load distribution data',
          type: 'error',
          position: 'top-right'
        });
        this.overviewPlot = {
          data: [],
          layout: {},
          config: {}
        };
      } finally {
        this.overviewParams.loading = false;
      }
    },
    runDifferentialExpression() {
      this.deResults.table.loading = true;
      // Reset to first page on a new run
      this.deResults.table.pagination.pageNum = 1;
      // Ensure marker gene groupBy follows the selected DE variable when running
      if (this.deParams && this.deParams.groupby) {
        this.markerParams.groupBy = this.deParams.groupby;
      }
      
      // Backend returns ALL genes (unfiltered), filtering happens client-side
      axios.post('/api/projectDetail/get_differential_expression', {
        datasetid: this.id,
        groupby: this.deParams.groupby,
        comparison_type: this.deParams.comparisonType,
        group1: this.deParams.group1,
        group2: this.deParams.group2,
        test_type: this.deParams.testType,
        log2fc_direction: this.deParams.foldChangeDirection,
        numeric_cutoff_method: this.deGroupbyIsNumeric() ? this.deParams.numericCutoffMethod : null,
        obs_filter: this.obsFilter
      }).then(response => {
        console.log(response.data);
        if (response.data.code == 200) {
          // Store raw (unfiltered) data - filtering happens via computed properties
          this.deResults.rawData = response.data.data || [];
          this.deResults.geneSymbols = response.data.geneSymbols;
          // Store DE group counts from backend
          this.$set(this.deResults, 'groupCounts', response.data.groupCounts || null)
          this.$set(this.deResults, 'groupSummary', response.data.groupSummary || null)
        } else {
          this.$notify({
            title: 'Error',
            message: response.data.error || 'Failed to load differential expression',
            type: 'error',
            position: 'top-right'
          });
        }
      }).catch(error => {
        // Extract the actual error message from backend if available
        const errorMsg = error.response && error.response.data && error.response.data.error 
          ? error.response.data.error 
          : error.message || 'Unknown error';
        this.$notify({
          title: 'Error',
          message: 'Differential expression error: ' + errorMsg,
          type: 'error',
          position: 'top-right'
        });
        console.error('DE Error:', error.response || error);
      }).finally(() => {
        this.deResults.table.loading = false;
      });
    },
    handleTablePageChange(page) {
      this.deResults.table.pagination.pageNum = page;
    },
    handleTablePageSizeChange(size) {
      this.deResults.table.pagination.pageSize = size;
      // Reset back to page 1 to avoid empty page
      this.deResults.table.pagination.pageNum = 1;
    },
    handleTableSortChange({ prop, order }) {
      this.deResults.table.sort = { prop, order };
    },
    formatNumber(row, column, cellValue) {
      if (cellValue == null || isNaN(cellValue)) return '';
      return Number(cellValue).toFixed(3);
    },
    formatPValue(row, column, cellValue) {
      if (cellValue == null || isNaN(cellValue)) return '';
      const val = Number(cellValue);
      return val < 0.001 ? val.toExponential(2) : val.toFixed(3);
    },
    formatFDR(row, column, cellValue) {
      if (cellValue == null || isNaN(cellValue)) return '';
      const val = Number(cellValue);
      return val < 0.001 ? val.toExponential(2) : val.toFixed(3);
    },
    formatPTooltip(val) {
      if (val == null || isNaN(val)) return '';
      const num = Number(val);
      return num < 0.001 ? num.toExponential(2) : num.toFixed(3);
    },
    formatLog10PTooltip(val) {
      if (val == null || isNaN(val)) return '';
      const log10Val = Number(val);
      const actualPval = Math.pow(10, -log10Val);
      // Show both -log10 value and actual p-value
      return `${log10Val.toFixed(2)} (p=${actualPval < 0.001 ? actualPval.toExponential(1) : actualPval.toFixed(3)})`;
    },
    formatPValueDisplay(val) {
      if (val == null || isNaN(val)) return '';
      const num = Number(val);
      if (num >= 0.01) return num.toFixed(2);
      if (num >= 0.001) return num.toFixed(3);
      if (num >= 0.0001) return num.toFixed(4);
      return num.toExponential(2);
    },
    handlePvalueScaleModeChange(newMode) {
      // Convert threshold values when switching scales
      if (newMode === 'log10') {
        // Convert from original to log10 scale
        if (this.deParams.pValueCutoff > 0) {
          this.deParams.pValueCutoff = -Math.log10(this.deParams.pValueCutoff);
        }
        if (this.deParams.fdrCutoff > 0) {
          this.deParams.fdrCutoff = -Math.log10(this.deParams.fdrCutoff);
        }
      } else {
        // Convert from log10 to original scale
        this.deParams.pValueCutoff = Math.pow(10, -this.deParams.pValueCutoff);
        this.deParams.fdrCutoff = Math.pow(10, -this.deParams.fdrCutoff);
      }
    },
    formatFoldChangeTooltip(val) {
      if (val == null || isNaN(val)) return '';
      return Number(val).toFixed(1) + '×';
    },
    onFoldChangeSlide(val) {
      // Expand max range only when user reaches the exact maximum
      if (val >= this.deParamRanges.foldChangeMax) {
        this.deParamRanges.foldChangeMax += 10;
      }
    },
    formatIntTooltip(val) {
      if (val == null || isNaN(val)) return '';
      const n = Math.round(Number(val));
      return n === 0 ? 'All' : String(n);
    },
    formatPercentTooltip(val) {
      if (val == null || isNaN(val)) return '';
      return `${(Number(val) * 100).toFixed(0)}%`;
    },
    deGroupbyIsNumeric() {
      try {
        const varName = this.deParams && this.deParams.groupby;
        const variables = Array.isArray(this.selectableObsVariables) ? this.selectableObsVariables : [];
        const variable = variables.find(v => v && v.name === varName);
        return variable && String(variable.inferred_type).toLowerCase() === 'numerical';
      } catch (e) {
        return false;
      }
    },
    // Format DE group option label with counts; prefer filtered counts from backend if available
    formatDeGroupLabel(val) {
      try {
        // Use backend groupCounts if provided
        const counts = (this.deResults && this.deResults.groupCounts) || null
        let n = null
        if (counts && Object.prototype.hasOwnProperty.call(counts, String(val))) {
          n = counts[String(val)]
        } else {
          const varName = this.deParams && this.deParams.groupby;
          const variables = Array.isArray(this.selectableObsVariables) ? this.selectableObsVariables : [];
          const variable = variables.find(v => v && v.name === varName);
          const list = variable && Array.isArray(variable.unique_values_n) ? variable.unique_values_n : [];
          const entry = list.find(e => (e && String(e.name)) === String(val));
          n = entry && (entry.number != null ? entry.number : (entry.count != null ? entry.count : entry.n));
        }
        if (n != null && isFinite(Number(n))) {
          return `${val} (n=${n})`;
        }
        return String(val);
      } catch (e) {
        return String(val);
      }
    },
    // Build the comparison title showing significant DE genes count
    generateExpressionData(n, mean, std) {
      return Array.from({ length: n }, () => this.normalRandom() * std + mean)
    },

    generateNormalData(n, mean, std) {
      return Array.from({ length: n }, () => this.normalRandom() * std + mean)
    },

    normalRandom() {
      // Box-Muller transform for normal distribution
      let u = 0, v = 0
      while (u === 0) u = Math.random() // Converting [0,1) to (0,1)
      while (v === 0) v = Math.random()
      return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v)
    },
    // Scroll indicator methods
    checkScroll() {
      const container = this.$refs.moduleButtons;
      if (!container) return;

      const { scrollLeft, scrollWidth, clientWidth } = container;

      // Show left arrow if we've scrolled to the right
      this.showLeftArrow = scrollLeft > 0;

      // Show right arrow if there's more content to scroll to
      // Add a small buffer (1px) to account for rounding
      this.showRightArrow = Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1;
    },

    scrollLeft() {
      const container = this.$refs.moduleButtons;
      if (!container) return;

      container.scrollBy({
        left: -200,
        behavior: 'smooth'
      });
    },

    scrollRight() {
      const container = this.$refs.moduleButtons;
      if (!container) return;

      container.scrollBy({
        left: 200,
        behavior: 'smooth'
      });
    },

    // Header height calculation
    calculateHeaderHeight() {
      this.$nextTick(() => {
        const header = this.$refs.fixedHeader;
        if (header) {
          this.headerHeight = header.offsetHeight;
        }
      });
    },

    // Toggle description visibility
    toggleDescription() {
      this.showDescription = !this.showDescription;
      // Recalculate header height when description visibility changes
      this.$nextTick(() => {
        this.calculateHeaderHeight();
      });
    },
    
  },



  watch: {
    // Reset pagination when thresholds change (client-side filtering)
    deCurrentThresholds: {
      handler() {
        this.deResults.table.pagination.pageNum = 1;
      },
      deep: true
    },
    'deParams.groupby'(newVal) {
      // Reset group selections when variable changes
      this.deParams.group1 = [];
      this.deParams.group2 = [];
      // Sync marker gene groupBy to selected DE variable
      this.markerParams.groupBy = newVal || '';
      this.markerParams.subgroupBy = '';
    },
    'deParams.group1'(newVal) {
      // Ensure no overlap: remove any values from group2 that are selected in group1
      const selected = Array.isArray(newVal) ? newVal : [];
      this.deParams.group2 = (Array.isArray(this.deParams.group2) ? this.deParams.group2 : [])
        .filter(v => !selected.includes(v));
    },
    'deParams.comparisonType'(newVal) {
      if (newVal === 'one_vs_rest') {
        // Hide group2 and clear it
        this.deParams.group2 = [];
      }
    },
    selectedModule(newModule) {
      if (newModule === 'clinicalCorrelation') {
        // Auto-run if inputs available
        this.$nextTick(() => {
          this.runClinicalCorrelation();
          this.runClinicalHeatmap();
          this.runCorrelationNetwork();
        });
      }
      if (newModule === 'markerGenes') {
        // Only preload suggestions; do not auto-select genes or group variable
        if (!(this.markerParams.gene_list && this.markerParams.gene_list.length)) {
          this.markerParams.gene_list = (this.project && this.project.geneSymbols ? this.project.geneSymbols.slice(0, 100) : []);
        }
      }
      if (newModule === 'clinicalCorrelation') {
        if (!(this.clinicalParams.gene_list && this.clinicalParams.gene_list.length)) {
          this.clinicalParams.gene_list = (this.project && this.project.geneSymbols ? this.project.geneSymbols.slice(0, 100) : []);
        }
      }
      if (newModule === 'geneFunction') {
        const isDifferentialDataset = this.project?.metadata?.datasetType === 'differential';
        if (isDifferentialDataset) {
          // For differential datasets, use selected genes from differential data module
          if (this.diffDataParams.selectedGenes && this.diffDataParams.selectedGenes.length > 0) {
            this.functionParams.selectedGenes = this.diffDataParams.selectedGenes.slice();
          } else if (this.diffDataGeneList && this.diffDataGeneList.length > 0) {
            // If no genes selected, use first 50 genes
            this.functionParams.selectedGenes = this.diffDataGeneList.slice(0, 50);
          }
        } else {
          if (!(this.functionParams.gene_list && this.functionParams.gene_list.length)) {
            this.functionParams.gene_list = (this.project && this.project.geneSymbols ? this.project.geneSymbols.slice(0, 100) : []);
          }
        }
        this.$nextTick(() => this.runGeneFunction())
      }
      if (newModule === 'molecularSubtyping') {
        // Load gene suggestions and annotations
        if (!(this.subtypeParams.gene_list && this.subtypeParams.gene_list.length)) {
          this.subtypeParams.gene_list = (this.project && this.project.geneSymbols ? this.project.geneSymbols.slice(0, 100) : []);
        }
        // Load available annotations if not loaded
        if (this.availableAnnotations.length === 0) {
          this.loadAvailableAnnotations();
        }
      }
      if (newModule === 'survivalAnalysis') {
        if (!(this.survivalParams.gene_list && this.survivalParams.gene_list.length)) {
          this.survivalParams.gene_list = (this.project && this.project.geneSymbols ? this.project.geneSymbols.slice(0, 100) : []);
        }
        // User must click "Run Survival Analysis" button to trigger analysis
      }
      if (newModule === 'functionalEnrichment') {
        const isDifferentialDataset = this.project?.metadata?.datasetType === 'differential';
        if (isDifferentialDataset) {
          // For differential datasets, use selected genes from differential data module
          if (this.diffDataParams.selectedGenes && this.diffDataParams.selectedGenes.length > 0) {
            this.enrichmentParams.selectedGenes = this.diffDataParams.selectedGenes.slice();
          } else if (this.diffDataGeneList && this.diffDataGeneList.length > 0) {
            // If no genes selected, use first 50 genes
            this.enrichmentParams.selectedGenes = this.diffDataGeneList.slice(0, 50);
          }
        } else {
          if (!(this.enrichmentParams.gene_list && this.enrichmentParams.gene_list.length)) {
            this.enrichmentParams.gene_list = (this.project && this.project.geneSymbols ? this.project.geneSymbols.slice(0, 100) : []);
          }
        }
        this.$nextTick(() => this.runEnrichment())
      }
      if (newModule === 'differentialDataTable') {
        // Auto-load differential data table when module is selected
        const isDifferentialDataset = this.project?.metadata?.datasetType === 'differential';
        if (isDifferentialDataset && this.diffDataResults.table.length === 0) {
          this.$nextTick(() => this.loadDifferentialDataTable())
        }
      }
    },
    activeSurvivalTab() {
      // User must click "Run Survival Analysis" button to trigger analysis
      // Tab switching does not auto-run
    },
    activeClinicalTab(newTab) {
      if (this.selectedModule === 'clinicalCorrelation' && newTab === 'network') {
        this.runCorrelationNetwork()
      }
    },
    // Survival params watchers removed - user must click "Run Survival Analysis" button

    // React to clinical correlation parameter changes
    'clinicalParams.selectedGenes'() {
      this.runClinicalCorrelation();
      this.runClinicalHeatmap();
      this.runCorrelationNetwork();
    },
    'clinicalParams.clinicalVariable'() {
      this.runClinicalCorrelation();
    },
    'clinicalParams.correlationMethod'() {
      this.runClinicalCorrelation();
      this.runClinicalHeatmap();
      this.runCorrelationNetwork();
    },
    'clinicalParams.networkAbsRThreshold'() {
      if (this.selectedModule === 'clinicalCorrelation') {
        this.applyNetworkThreshold();
      }
    },
    // Gene Function
    'functionParams.selectedGenes'() { if (this.functionParams.searchMode === 'genes') this.runGeneFunction() },
    'functionParams.textQuery'() { /* do not search on each keystroke; enter/click triggers */ },
    'enrichmentParams.selectedGenes'() {
      this.runEnrichment()
    },

    sidebarCollapsed() {
      // Recalculate header height when sidebar state changes
      this.calculateHeaderHeight();
    }
  },

  mounted() {
    this.getProjectDetail();
    // Initialize scroll indicators and header height
    // Use a small delay to ensure DOM is fully rendered
    setTimeout(() => {
      this.checkScroll();
      this.calculateHeaderHeight();

      // Create and store resize handler for proper cleanup
      // Debounced to improve performance during resize
      this.resizeHandler = () => {
        this.checkScroll();
        this.calculateHeaderHeight();
        
        // Debounce the window resize key update for plot recalculation
        // This prevents excessive re-renders during continuous resize
        if (this.resizeDebounceTimer) {
          clearTimeout(this.resizeDebounceTimer);
        }
        this.resizeDebounceTimer = setTimeout(() => {
          // Increment to trigger reactive recalculation of heatmap layout
          this.windowResizeKey++;
        }, 150); // 150ms debounce - fast enough to feel responsive, slow enough to avoid thrashing
      };

      // Add resize listener
      window.addEventListener('resize', this.resizeHandler);
    }, 100);

    // Prevent swipe-back/back navigation on this view (iOS/macOS gesture)
    this.popstateHandler = () => {
      history.go(1);
    };
    history.pushState(null, '', document.URL);
    window.addEventListener('popstate', this.popstateHandler);
  },

  beforeDestroy() {
    // Restore original browser tab title
    document.title = '景杰生物数据平台';
    
    // Clean up resize listener
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    // Clean up resize debounce timer
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
      this.resizeDebounceTimer = null;
    }
    // Clean up popstate handler
    if (this.popstateHandler) {
      window.removeEventListener('popstate', this.popstateHandler);
      this.popstateHandler = null;
    }
  }
}
</script>

<style scoped>
/* Scrollable multi-select for protein/gene selectors */
.scrollable-select {
  width: 100%;
}

.scrollable-select ::v-deep .el-select__tags {
  max-height: 80px;
  overflow-y: auto;
  flex-wrap: wrap;
}

.scrollable-select ::v-deep .el-select__tags::-webkit-scrollbar {
  width: 4px;
}

.scrollable-select ::v-deep .el-select__tags::-webkit-scrollbar-thumb {
  background: #c1c9d0;
  border-radius: 2px;
}

.scrollable-select ::v-deep .el-select__tags::-webkit-scrollbar-track {
  background: transparent;
}

/* Ensure clear button stays visible */
.scrollable-select ::v-deep .el-input__suffix {
  right: 5px;
}


.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa, #c3cfe2);
}

/* Navigate Bar - Search Filter and Project Summary Header */
.navigate-bar {
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.header {
  padding: 5px 5px 5px 5px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-button {
  color: #3b82f6;
  font-weight: 500;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  flex-shrink: 0;
  font-size: 14px;
  min-width: 40px;
  height: 40px;
}

.description-toggle-button {
  color: #3b82f6;
  font-weight: 500;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  flex-shrink: 0;
  font-size: 14px;
  min-width: 40px;
  height: 40px;
  margin-left: auto;
}

.description-toggle-button:hover {
  background-color: #f1f5f9;
}

.content-section {
  padding: 14px;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  margin: 0 10px 10px 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  min-height: 0;
}

/* Module wrapper divs - fill available space */
.content-section > div {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* Scrollable modules (like dataset introduction) - allow content to expand naturally */
.content-section > div.module-scrollable {
  display: block;
  flex: none;
  overflow: visible;
}

/* When scrollable module is active, content-section handles the scroll */
.content-section:has(.module-scrollable) {
  overflow-y: auto;
}

/* Main Module Layout System */
.module-layout {
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: 16px;
  overflow: hidden;
  min-height: 0;
}

/* Control Panel System */
.parameter-panel {
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  flex: 0 0 350px; /* Fixed width, no grow/shrink */
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.parameter-panel h4 {
  /* background: #f8f9fa; */
  background: linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
  margin:0;
  padding: 8px 16px;
  color: #2c3e50;
  font-weight: 600;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.parameter-panel h4 > i {
  color: #3b82f6;
  font-size: 1rem;
}

/* Filter icon button in parameter header */
.filter-btn-wrapper {
  margin-left: auto;
}

.filter-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-icon-btn:hover {
  background: #f1f5f9;
  border-color: #3b82f6;
  color: #3b82f6;
}

.filter-icon-btn i {
  font-size: 14px;
}

.filter-icon-btn.has-filters {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #3b82f6;
}

.parameter-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 15px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.parameter-subsection {
  padding: 8px;
  background: transparent;
}

.parameter-footer {
  border-top: dotted;
  border-top-color: #c3cfe2;
  background: #f3f4f6;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  box-shadow: 0 -6px 20px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

/* Results Panel System */
.results-panel {
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.results-panel h4 {
  background: #f8f9fa;
  margin: 0;
  padding: 12px 16px;
  color: #2c3e50;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.results-panel h4 > i {
  color: #3b82f6;
  font-size: 1rem;
}

/* Results Header Actions */
.results-actions {
  display: flex;
  gap: 6px;
  margin-left: 12px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.action-btn--success {
  background: #ecfdf5;
  color: #059669;
}

.action-btn--success:hover:not(:disabled) {
  background: #d1fae5;
}

.action-btn--danger {
  background: #fef2f2;
  color: #dc2626;
}

.action-btn--danger:hover:not(:disabled) {
  background: #fee2e2;
}

.action-btn i {
  font-size: 11px;
}

/* Tab Switcher in Results Header */
.tab-switcher {
  margin-left: auto;
}

.tab-switcher ::v-deep .el-radio-group {
  display: inline-flex;
}

.tab-switcher ::v-deep .el-radio-button__inner {
  padding: 5px 12px;
  font-size: 12px;
  border-color: #e5e7eb !important;
}

.tab-switcher ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: #3b82f6;
  border-color: #3b82f6 !important;
  box-shadow: none;
}

/* Results Summary Badges */
.results-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.summary-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 12px;
}

.summary-badge i {
  font-size: 10px;
}

.summary-badge--success {
  background: #3b82f6;
  color: #ffffff;
}

.summary-badge--clickable {
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid transparent;
  padding-left: 6px;
  padding-right: 8px;
}

.summary-badge--clickable i {
  margin-right: 3px;
  font-size: 11px;
}

.summary-badge--clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
  background: #2563eb;
}

.summary-badge--clickable:active {
  transform: translateY(0);
  background: #1d4ed8;
}

.summary-badge--warning {
  background: #fef3c7;
  color: #d97706;
}

.summary-badge--warning-alt {
  background: #ffedd5;
  color: #ea580c;
}

.summary-badge--muted {
  background: #f1f5f9;
  color: #94a3b8;
}

.summary-badge--info {
  background: #dbeafe;
  color: #1d4ed8;
}

.summary-badge--venn {
  background: #f3e8ff;
  color: #7c3aed;
  cursor: pointer;
  transition: all 0.15s ease;
}

.summary-badge--venn:hover {
  background: #ede9fe;
  transform: translateY(-1px);
}

/* Threshold Toolbar in Results */
.threshold-toolbar {
  display: flex;
  gap: 12px;
  margin: 10px 5px;
  margin-bottom: 0;
  flex-wrap: wrap;
  align-items: stretch;
}

.threshold-section {
  display: flex;
  gap: 16px;
  padding: 12px 16px;
  background: #f1f5f9;
  border-radius: 10px;
  align-items: flex-end;
}

.threshold-section-left {
  flex: 1;
}

.threshold-section-right {
  flex: 2;
}

.threshold-item {
  flex: 1;
  min-width: 160px;
}

.threshold-item ::v-deep .de-param-slider {
  margin-bottom: 0;
}

.threshold-switch {
  flex: 0 0 auto;
  min-width: auto;
  display: flex;
  align-items: flex-start;
}

/* Match parameter panel's pill-style radio buttons - vertical layout */
.threshold-switch ::v-deep .el-radio-group {
  display: flex;
  flex-direction: column;
  background: #e2e8f0;
  border-radius: 6px;
  padding: 3px;
  gap: 2px;
}

.threshold-switch ::v-deep .el-radio-button {
  width: 100%;
}

.threshold-switch ::v-deep .el-radio-button__inner {
  border: none !important;
  background: transparent;
  border-radius: 6px !important;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  transition: all 0.2s ease;
  box-shadow: none !important;
  margin: 0;
  width: 100%;
  text-align: center;
}

.threshold-switch ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: #3b82f6;
  color: #ffffff;
  box-shadow: none !important;
}

.threshold-switch ::v-deep .el-radio-button__inner:hover {
  color: #3b82f6;
}

.threshold-switch ::v-deep .el-radio-button:first-child .el-radio-button__inner,
.threshold-switch ::v-deep .el-radio-button:last-child .el-radio-button__inner {
  border-radius: 6px !important;
}

.results-container {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  /* Important for nested scrolling inside flex layouts (prevents the container
     from expanding to fit children and causing "double scroll" / weird overflow). */
  min-height: 0;
}

.results-toolbar {
  border:1px solid #e2e8f0; 
  padding:8px 14px; 
  border-radius:6px; 
  background-color: #f1f5f9;
  display: flex;
  gap: 12px;
  align-items: center;
  margin: 8px;
}

/* Table System */
.table-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
}

.table-container {
  height: 100%;
  overflow: auto;
}

/* Differential Data Table Specific Styles */
.diff-data-table-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
}

.diff-data-full-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.diff-data-pagination-top {
  flex-shrink: 0;
  padding: 16px 24px;
  display: flex;
  justify-content: center;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.diff-data-table-scroll {
  flex: 1;
  overflow: hidden;
}

.diff-data-table-scroll .el-table {
  height: 100% !important;
}

.diff-data-table-scroll .el-table__body-wrapper {
  overflow-y: auto !important;
}

/* Plot System */
.plot-container {
  padding: 0;
  /* Let plot areas scroll within the results panel, consistently across modules. */
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.enrichment-plot-container {
  padding: 24px;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
}
.plot-toolbar {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
  margin: 8px;
  font-weight: 600;
}
.plot-toolbar span { text-align: center; }

.plot-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.plot-sequence {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  width: 90%;
  max-width: 1000px;
  margin: 0 auto;
}

/* Apply same gap to nested containers within plot-sequence */
.plot-sequence > div {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.plot-item-header {
  background-color: #f0f0f0;
  padding: 5px;
  margin: 10px !important;
  border-radius: 5px;
  text-align: center;
}

/* Styling only for actual plots, not placeholders */
.plot-sequence .plot-item-actual {
  background: white;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
}

.plot-sequence .plot-item-actual:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.plot-sequence .plot-item-average {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 3px solid #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
  position: relative;
}

.plot-sequence .plot-item-average::before {
  content: "AVERAGE EXPRESSION";
  position: absolute;
  top: -12px;
  left: 20px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
}

.plot-item-gene-title {
  margin: -16px -16px 12px -16px !important;
  padding: 10px 16px !important;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #1e293b !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  text-align: center !important;
  border-bottom: 2px solid #e2e8f0 !important;
  border-radius: 6px 6px 0 0 !important;
  letter-spacing: 0.3px !important;
}

/* Module System - Folder Tab Design */
.module-buttons-container {
  position: relative;
  display: flex;
  align-items: flex-end;
  background: transparent;
  padding: 8px 10px 0 10px;
  overflow: hidden;
  border-bottom: none;
}

.module-buttons {
  display: flex;
  flex-wrap: nowrap;
  gap: 3px;
  margin: 0;
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  overflow-x: auto;
  flex: 1;
  -ms-overflow-style: none;
  scrollbar-width: none;
  align-items: flex-end;
}

.module-buttons::-webkit-scrollbar {
  display: none;
}

.module-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border: 1px solid #cbd5e1;
  border-bottom: none;
  border-radius: 12px 12px 0 0;
  background: #e2e8f0;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  position: relative;
  letter-spacing: 0.01em;
  margin-bottom: -1px;
  z-index: 1;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.08);
}

.module-btn:hover {
  background: #cbd5e1;
  color: #3b82f6;
}

.module-btn:hover i {
  transform: translateY(-1px);
}

.module-btn.active {
  padding: 13px;
  font-size: 15px;
  background: #ffffff;
  color: #3b82f6;
  border-color: #e2e8f0;
  z-index: 2;
  margin-top: -1px;
  box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.05);
}

/* Curved corner connectors for active tab */
.module-btn.active::before,
.module-btn.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  width: 12px;
  height: 12px;
  background: transparent;
}

.module-btn.active::before {
  left: -12px;
  border-radius: 0 0 12px 0;
  box-shadow: 6px 0 0 0 #ffffff;
}

.module-btn.active::after {
  right: -12px;
  border-radius: 0 0 0 12px;
  box-shadow: -6px 0 0 0 #ffffff;
}

.module-btn.active:hover {
  background: #ffffff;
  color: #2563eb;
}

.module-btn.active i {
  color: #3b82f6;
}

.module-btn i {
  font-size: 14px;
  transition: all 0.2s ease;
}

/* Sub-tabs - Folder Style (for inner tabs like DE volcano/table) */
.sub-tabs {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 6px 16px 0 16px;
  background: linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
}

.sub-tabs-left {
  display: flex;
  gap: 2px;
}

.sub-tabs-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 6px;
}

/* Larger badges in sub-tabs actions */
.sub-tabs-actions .summary-badge {
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 16px;
}

.sub-tabs-actions .summary-badge i {
  font-size: 14px;
}

.sub-tab {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  border: 1px solid transparent;
  border-bottom: none;
  border-radius: 10px 10px 0 0;
  background: transparent;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  margin-bottom: -1px;
}

.sub-tab:hover {
  background: rgba(255, 255, 255, 0.6);
  color: #3b82f6;
}

.sub-tab.active {
  background: #ffffff;
  color: #3b82f6;
  border-color: #e2e8f0;
  font-weight: 600;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.03);
}

.sub-tab.active i {
  color: #3b82f6;
}

.sub-tab i {
  font-size: 13px;
}

/* Small action button variant */
.action-btn--small {
  padding: 4px 8px;
  font-size: 12px;
}

/* Scroll Indicators - Folder Style */
.scroll-indicator {
  position: absolute;
  bottom: 0;
  width: 28px;
  height: calc(100% - 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(241, 245, 249, 0.95);
  cursor: pointer;
  opacity: 0;
  visibility: hidden;
  transition: all 0.25s ease;
  z-index: 10;
}

.scroll-indicator:hover {
  background: rgba(226, 232, 240, 0.95);
}

.scroll-indicator:hover i {
  color: #1e40af;
  transform: scale(1.2);
}

.scroll-indicator:active i {
  transform: scale(0.9);
}

.scroll-indicator.left {
  left: 0;
  border-radius: 0 6px 0 0;
  box-shadow: 2px 0 6px rgba(0, 0, 0, 0.08);
}

.scroll-indicator.right {
  right: 0;
  border-radius: 6px 0 0 0;
  box-shadow: -2px 0 6px rgba(0, 0, 0, 0.08);
}

.scroll-indicator.visible {
  opacity: 1;
  visibility: visible;
}

.scroll-indicator i {
  font-size: 14px;
  color: #64748b;
  font-weight: 600;
  transition: all 0.2s ease;
}

.dataset-overview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.overview-card {
  background: #f8f9fa;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.overview-plot-card {
  display: flex;
  flex-direction: column;
  height: 500px;
  padding: 0;
  overflow: hidden;
}

.overview-plot-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 16px;
}

.overview-plot-container .js-plotly-plot {
  width: 100% !important;
  height: 100% !important;
}

.overview-card h4 {
  margin-bottom: 20px;
  color: #2c3e50;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.stat-item {
  text-align: center;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-number {
  display: block;
  font-size: 2rem;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
}

.dataset-description {
  background: #f8f9fa;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.dataset-description h4 {
  margin-bottom: 16px;
  color: #2c3e50;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dataset-description p {
  margin-bottom: 16px;
  line-height: 1.6;
  color: #495057;
}

.dataset-description ul {
  margin: 0;
  padding-left: 20px;
}

.dataset-description li {
  margin-bottom: 8px;
  line-height: 1.5;
  color: #495057;
}

.dataset-description strong {
  color: #2c3e50;
}

.expand-grid {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 8px 16px;
  padding: 12px 8px;
  background: #fafafa;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
}
.expand-grid .kv {
  display: contents;
}
.expand-grid .k {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}
.expand-grid .v {
  color: #111827;
  font-size: 12px;
  word-break: break-word;
}

.image-container {
  padding: 24px;
}

.plot-placeholder {
  background: #f8fafc;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  padding: 48px;
  text-align: center;
  color: #64748b;
}

/* Placeholders inside containers get automatic margin */
.plot-container .plot-placeholder,
.table-container .plot-placeholder,
.results-container .plot-placeholder {
  margin: 24px;
}

/* plot-sequence already has padding, so reset placeholder margin */
.plot-sequence .plot-placeholder {
  margin: 0;
}

.plot-placeholder i {
  font-size: 2.5rem;
  display: block;
  margin-bottom: 12px;
  opacity: 0.6;
}

.plot-placeholder p {
  margin: 6px 0 2px;
  font-size: 1rem;
}

.plot-placeholder small {
  display: block;
  opacity: 0.8;
}

.parameter-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.parameter-group label {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
}

/* Enhanced select */
.enhanced-select { width: 100%; }

/* Variable tag in dropdown */
.option-content { 
  display: inline-flex; 
  align-items: baseline;
  gap: 8px;
  height: auto;
}

.variable-tag {
  display: inline-block;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 3px;
  border: 1px solid;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.4;
  vertical-align: middle;
  margin-right: 8px;
}

.variable-name {
  vertical-align: middle;
}


/* Utility Classes */
.ml-auto { margin-left: auto; }
.mt-8 { margin-top: 8px; }
.full-width { width: 100%; }
.flex-1 { flex: 1; }
.required { color: #f56c6c; }
.field-label { font-size: 14px; color: #6c757d; font-weight: 500; display: block; margin-bottom: 4px; }

/* Reusable Step Sections */
.step-section { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; margin-bottom: 10px; background: #fafafa; }
.step-header { display: flex; align-items: center; margin-bottom: 10px; gap: 8px; }
.step-title { font-size: 14px; font-weight: 600; color: #334155; }
.section-icon { color: #64748b; font-size: 0.9rem; }
.header-btn { padding: 2px 6px; font-size: 12px; }

/* Compact Parameters (shared between DE and Marker) */
.compact-params .step-section,
.de-params .step-section { padding: 10px; margin-bottom: 10px; }
.compact-params .step-header,
.de-params .step-header { margin-bottom: 8px; }

/* Inline fields layout */
.inline-fields { display: flex; gap: 20px; flex-wrap: wrap; align-items: flex-start; }
.inline-fields .field { display: flex; flex-direction: column; gap: 6px; }
.inline-fields .field .field-label { margin-bottom: 0; }

/* Select with tag prefix */
.select-with-tag { display: flex; align-items: center; gap: 6px; }
.select-with-tag .variable-tag { flex-shrink: 0; }

/* Inline options (switches) */
.inline-options { display: flex; gap: 12px; flex-wrap: wrap; }
.option-toggle { display: flex; align-items: center; gap: 6px; padding: 6px 10px; background: white; border: 1px solid #e1e4e8; border-radius: 4px; cursor: pointer; font-size: 0.8rem; color: #586069; font-weight: 500; }
.option-toggle:hover { border-color: #3b82f6; background: #f6f8fa; }

/* DE comparison radio - full width 50/50 split */
.de-comparison-radio { width: 100%; display: flex; }
.de-comparison-radio ::v-deep .el-radio-button { flex: 1; }
.de-comparison-radio ::v-deep .el-radio-button__inner { width: 100%; text-align: center; }

/* Parameter panel radio styles */
.parameter-panel .el-radio { margin-right: 8px; margin-bottom: 4px; }
.parameter-panel .scale-switch { transform: scale(0.85); }

/* Elegant Radio Button Group (pill-style segmented control) - applies to all parameter panels */
.parameter-panel ::v-deep .el-radio-group {
  display: inline-flex;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
  gap: 0;
}

.parameter-panel ::v-deep .el-radio-button__inner {
  border: none !important;
  background: transparent;
  border-radius: 6px !important;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  transition: all 0.2s ease;
  box-shadow: none !important;
  margin: 0;
}

.parameter-panel ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: #ffffff;
  color: #3b82f6;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.parameter-panel ::v-deep .el-radio-button__inner:hover {
  color: #3b82f6;
}

.parameter-panel ::v-deep .el-radio-button:first-child .el-radio-button__inner {
  border-radius: 6px !important;
  margin-left: 0;
}

.parameter-panel ::v-deep .el-radio-button:last-child .el-radio-button__inner {
  border-radius: 6px !important;
}

/* Compact sliders */
.compact-slider {
  padding: 0 2px;
}
.compact-slider ::v-deep .el-slider__runway {
  height: 2px;
  /* Element-UI's default vertical spacing is tuned for a thicker runway.
     With a 2px runway we tighten the margin so the slider reads visually centered. */
  margin: 8px 0;
  background-color: #e5e7eb; /* light gray for contrast on white panel */
}
.compact-slider ::v-deep .el-slider__bar {
  height: 2px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}
.compact-slider ::v-deep .el-slider__button-wrapper {
  /* Re-center the thumb wrapper on the (now thinner) runway */
  top: 50%;
  transform: translate(-50%, -50%);
}
.compact-slider ::v-deep .el-slider__button {
  width: 10px;
  height: 10px;
  border: 2px solid #3b82f6;
  background: #ffffff;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
}
.compact-slider ::v-deep .el-slider__button:hover,
.compact-slider ::v-deep .el-slider__button:focus {
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.25);
}

/* Run Analysis button styling */
.run-analysis-btn {
  width: 100%;
  height: 40px;
  font-weight: 600;
  letter-spacing: 0.02em;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
  transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease;
  color: white !important;
}

.run-analysis-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(16, 185, 129, 0.5);
  filter: brightness(1.08);
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.run-analysis-btn:active {
  transform: translateY(0);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
  filter: brightness(1.0);
}

.run-analysis-btn.is-disabled,
.run-analysis-btn.is-loading {
  filter: grayscale(30%) brightness(0.85);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
  opacity: 0.6;
}

.run-analysis-btn .el-icon-caret-right {
  font-size: 14px;
  margin-right: 4px;
}

/* Gene hint styles */
.gene-hint {
  color: #6c757d;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.gene-hint.selected {
  color: #3b82f6;
  font-weight: 500;
}

/* Table hover effects */
.el-table tbody tr {
  cursor: pointer;
  transition: all 0.3s ease;
}

/* Survival plots grid */
.survival-plots-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  align-items: flex-start;
  padding: 12px;
}


.page-title {
  font-size: 1.5rem;
}

.project-not-found {
  text-align: center;
  margin-top: 50px;
}

.project-not-found h1 {
  font-size: 2em;
  color: #ff6b6b;
}

.project-not-found p {
  margin: 20px 0;
  font-size: 1.2em;
}

.btn-return {
  background-color: #3498db;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.btn-return:hover {
  background-color: #2980b9;
}

.clinical-scatter-plot ::v-deep .xtitle,
.clinical-scatter-plot ::v-deep .ytitle,
.clinical-scatter-plot ::v-deep .xtick text,
.clinical-scatter-plot ::v-deep .ytick text {
  letter-spacing: 2px;
}
</style>
